import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Activity,
  ArrowRight,
  BarChart3,
  BellRing,
  BookOpen,
  Check,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  Compass,
  Eye,
  FileQuestion,
  Layers3,
  Mail,
  Menu,
  MessageCircle,
  Plus,
  Shield,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  X,
  Zap,
  Handshake,
  type LucideIcon,
} from "lucide-react";
import React, { FormEvent, ReactNode, useEffect, useState } from "react";
import { Link } from "wouter";
import { Hero2Section } from "@/pages/Hero2";
import { SEO } from "@/components/SEO";
import { GooglePreferredSourcesButton } from "@/components/GooglePreferredSources";
import { StructuraSoftwareSchema } from "@/components/StructuraSoftwareSchema";
import readArticlesData from "@/data/readArticles.generated.json";
import { VideoCard } from "@/components/VideoCard";
import { toolVideos } from "@shared/toolVideos.mjs";
import { HomeOverviewVideoSchema, HomeOverviewVideoSection } from "@/components/HomeOverviewVideo";

type PlanKey = "compass" | "vector" | "vantage";
type BillingPeriod = "monthly" | "yearly";

type Tool = {
  name: string;
  layer: string;
  promise: string;
  description: string;
  technical: string[];
  image: string;
  icon: LucideIcon;
  accent: string;
};

type Person = {
  name: string;
  role: string;
  label: string;
  photo: string;
  bio: string;
};

const checkoutLinks: Record<PlanKey, Record<BillingPeriod, string>> = {
  // TODO(pricing-launch): Replace these temporary legacy checkout product IDs with the new Compass, Vector, and Vantage IDs supplied by Marcin before publishing.
  compass: {
    monthly: "https://store.payproglobal.com/checkout?products[1][id]=116620&page-template=21899",
    yearly: "https://store.payproglobal.com/checkout?products[1][id]=135253&page-template=21899",
  },
  vector: {
    monthly: "https://store.payproglobal.com/checkout?products[1][id]=116623&page-template=21899",
    yearly: "https://store.payproglobal.com/checkout?products[1][id]=135252&page-template=21899",
  },
  vantage: {
    monthly: "https://store.payproglobal.com/checkout?products[1][id]=135255&page-template=21899",
    yearly: "https://store.payproglobal.com/checkout?products[1][id]=135254&page-template=21899",
  },
};

const tools: Tool[] = [
  {
    name: "Accumulate",
    layer: "Structure layer",
    promise:
      "See where the market may be building structure before the move becomes obvious.",
    description:
      "Accumulate helps separate noisy price movement from zones where the market may be rebuilding balance and preparing the next phase.",
    technical: [
      "structure zones",
      "accumulation context",
      "no buy/sell signals",
    ],
    image: "/accumulate-demo.webp",
    icon: Layers3,
    accent: "emerald",
  },
  {
    name: "Horizon",
    layer: "Momentum layer",
    promise: "Check whether momentum supports the structural shift.",
    description:
      "Horizon organizes the momentum read so users do not judge the market only by the latest candle, but by the broader transition context.",
    technical: ["momentum context", "phase shift", "overextension filter"],
    image: "/horizon-demo.webp",
    icon: Compass,
    accent: "cyan",
  },
  {
    name: "Pulse",
    layer: "Trend behavior layer",
    promise:
      "Confirm whether trend behavior is starting to support the scenario.",
    description:
      "Pulse helps assess whether trend behavior is actually confirming structure and momentum instead of producing another conflicting signal.",
    technical: ["trend behavior", "phase confirmation", "directional clarity"],
    image: "/pulse-demo.webp",
    icon: Activity,
    accent: "violet",
  },
  {
    name: "Sentinel",
    layer: "Scanning layer",
    promise: "Scan markets and timeframes for conditions worth your attention.",
    description:
      "Sentinel reduces manual chart scanning time and directs attention to where structure, momentum, or trend behavior is developing.",
    technical: ["market scanning", "alert workflow", "analysis priorities"],
    image: "/sentinel-demo.png",
    icon: BellRing,
    accent: "blue",
  },
];

const resultsTestimonials = [
  {
    initials: "NY",
    name: "Naci Y.",
    role: "Community member",
    quote: "I made several long-term trades based on TrendGo charts. During turbulent moments in the Turkish market, TrendGo gave me clarity and confidence to avoid panic moves. After just a month, I went for the annual subscription. It gives me peace of mind.",
    highlight: "clarity and confidence to avoid panic moves",
  },
  {
    initials: "KP",
    name: "Kaushal P.",
    role: "Community member",
    quote: "TrendGo is a value creator and I am very happy to be a lifetime subscriber. It not only saves time but also increases accuracy of the trade without needing multiple indicators. TrendGo branding should be 'Just Works' like Nike's 'Just Do It'.",
    highlight: "increases accuracy of the trade without needing multiple indicators",
  },
  {
    initials: "DS",
    name: "Dominik S.",
    role: "Community member",
    quote: "TrendGo nails tops, bottoms, divergences and confirmation move at zero-line break; it really helps with decision-making. I love how clean the charts are - that alone made me switch fully to TrendGo and ditch other indicators.",
    highlight: "made me switch fully to TrendGo and ditch other indicators",
  },
  {
    initials: "DS",
    name: "Davide S.",
    role: "Annual subscriber",
    quote: "I enjoy watching TrendGo grow. The founders are super responsive, and their Telegram analyses are a solid reference point for me. I subscribed annually because I know this is a tool I'll use daily - a real investment in my trading workflow.",
    highlight: "a real investment in my trading workflow",
  },
  {
    initials: "KW",
    name: "Karol W.",
    role: "Community member",
    quote: "I tried all sorts of indicators, but TrendGo is different - I actually feel it working. I love using it on lower timeframes, it gives me real confidence, and I want to stick with it long-term.",
    highlight: "it gives me real confidence",
  },
  {
    initials: "MS",
    name: "Mindy S.",
    role: "Member since 2025",
    quote: "I use Accumulate and Horizon the most. Can't live without those.",
    highlight: "Can't live without those",
  },
];

const featuredStefanoTestimonial = {
  name: "Stefano Bottaioli",
  role: "Professional Investment Advisor & TrendGo Ambassador",
  label: "Professional Insight",
  photo:
    "/team/stefano-bottaioli.jpg",
  quote:
    "In a short time, I've seen how profitable using the TrendGo trading system can be. Market Moment Analyzer helped me spot entries and exits I wouldn't have seen otherwise. This tool deserves to be recommended.",
  highlight: "spot entries and exits I wouldn't have seen otherwise",
};

const caseStudies = [
  {
    asset: "ORCL",
    title: "Structure → Momentum → Trend",
    metric: "3 confirmation layers",
    image: "/case-studies/orcl-accumulate-horizon-pulse.png?v=round22",
    copy: "Structure appeared first; momentum and trend behavior later began confirming the broader context.",
  },
  {
    asset: "NVDA",
    title: "Trend reset into structure",
    metric: "clear structure retest",
    image: "/case-studies/nvda-accumulate-pulse.png?v=round22",
    copy: "A strong trend returned toward structure, making continuation easier to assess without chasing the move after the fact.",
  },
  {
    asset: "AAPL",
    title: "Distance from structure",
    metric: "price-extension context",
    image: "/case-studies/aapl-accumulate-horizon.webp?v=round22",
    copy: "Price became easier to interpret once its distance from structure was shown inside a single workflow.",
  },
  {
    asset: "NFLX",
    title: "Accumulation before expansion",
    metric: "accumulation + momentum",
    image: "/case-studies/nflx-accumulation-detected.png?v=round22",
    copy: "This retrospective example shows how structure, momentum recovery, and behavior confirmation can form a readable sequence.",
  },
  {
    asset: "COIN",
    title: "Weekly structure read",
    metric: "higher-timeframe context",
    image: "/case-studies/coin-weekly-structure.png?v=round22",
    copy: "A weekly structure example showing how a broader timeframe can frame a more disciplined market read.",
  },
  {
    asset: "GRAB",
    title: "Structure building phase",
    metric: "base-building context",
    image: "/case-studies/grab-structure-building.png?v=round22",
    copy: "A retrospective example of structure building before a market phase becomes easier to evaluate.",
  },
];

const team: Person[] = [
  {
    name: "Paweł Surdyk",
    role: "Founder · Business Development",
    label: "Product direction",
    photo:
      "/team/pawel-surdyk.png",
    bio: "Spent years analyzing market structure across equities, crypto, and forex before building Structura. Leads product direction, partnerships, and translates real trading problems into a focused product roadmap.",
  },
  {
    name: "Marcin Środa",
    role: "Lead Developer",
    label: "Technical Architecture",
    photo:
      "/team/marcin-sroda.jpg",
    bio: "Builds Structura’s technical architecture, tool logic, and the path from market concepts to usable product experiences.",
  },
  {
    name: "Pierce Crosby",
    role: "Advisor · Merchant Seven",
    label: "Platform Experience",
    photo: "/team/pierce-crosby.webp",
    bio: "Brings experience in trading platforms, fintech strategy, and the expectations of active market users.",
  },
  {
    name: "Stefano Bottaioli",
    role: "Ambassador · Professional Investment Advisor",
    label: "Market Practitioner",
    photo:
      "/team/stefano-bottaioli.jpg",
    bio: "Adds a practitioner perspective on markets, risk management, cycles, and interpreting accumulation in real conditions.",
  },
];

const navItems = [
  { label: "Product", href: "/product" },
  { label: "Free Tools", href: "/free-tools" },
  { label: "Read", href: "/read" },
  { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
  { label: "Reviews", href: "/testimonials" },
  { label: "Education", href: "/education" },
  { label: "Support", href: "/support" },
  { label: "Contact", href: "/contact" },
];

const footerReadLinks = readArticlesData.slice(0, 3).map(article => ({
  label: article.title,
  href: article.slug,
}));

const pricingPlans = [
  {
    key: "compass" as const,
    name: "Compass",
    label: "Core stack",
    monthly: "$29",
    yearly: "$290",
    description:
      "The four layers that read a single chart, plus every free layer. Enough to read a chart the way we read it on Sundays.",
    features: [
      "Horizon",
      "Pulse",
      "Accumulate, Divergence and Blocks (free layers)",
      "TradingView access",
      "TrendSpider compatible (own subscription)",
      "Case study updates",
      "14-day free trial",
    ],
    cta: "Get Compass",
  },
  {
    key: "vector" as const,
    name: "Vector",
    label: "Full system + Prism",
    monthly: "$89",
    yearly: "$890",
    description:
      "Compass, plus Sentinel, the two new layers and Prism - the read, written for you, on any chart.",
    features: [
      "Prism (AI assistant) - 100 reads a month",
      "Liquidity and Squeeze (new)",
      "Sentinel scanner",
      "Horizon, Pulse and every free layer",
      "Multi-timeframe workflow",
      "Community: Telegram room and subscriber Discord",
      "Weekly market structure notes",
      "Priority support",
      "14-day free trial",
    ],
    highlighted: true,
    cta: "Get Vector",
  },
  {
    key: "vantage" as const,
    name: "Vantage",
    label: "Everything + the human layer",
    monthly: "$199",
    yearly: "$1,990",
    description:
      "Vector, plus the human layer: onboarding, direct support and first access to what we build next.",
    features: [
      "Prism (AI assistant) - 150 reads a month",
      "Everything in Vector",
      "Onboarding call",
      "Exclusive support",
      "Custom workflow support",
      "Advanced education",
      "Priority access to new tools",
      "14-day free trial",
    ],
    cta: "Get Vantage",
  },
];

const billingFaq: { q: string; a: React.ReactNode }[] = [
  {
    q: "Which plan includes Prism?",
        a: (
          <>
        Prism, our AI market structure assistant, is included in Vector and Vantage. Vector includes 100 Prism reads a month, Vantage 150. Compass does not include Prism. Prism runs in the Structura web app, so it works whichever charting platform you use. {" "}
        <Link href="/manual#prism" className="text-[#33CDD3] underline underline-offset-2 transition-colors hover:text-white">
          See how Prism reads →
        </Link>
      </>
    ),
  },
  {
    q: "What is a Prism read?",
    a: "One question, one read. Ask about a symbol and timeframe - or several timeframes, or your whole watchlist in one question - and Prism writes the read: which layers agree, which disagree, which are silent, and how ready the system is. Each question counts as one read. Your allowance resets every month. When it is used up, Prism tells you; nothing is charged automatically.",
  },
  {
    q: "Does Prism tell me what to trade?",
    a: "No. Prism describes the state of the layers on your chart and the layers we do not publish. It names what agrees, what blocks the read, and what would change it. It does not issue actions or levels. It is educational analysis, not financial advice.",
  },
  {
    q: "I already subscribe. What changes for me?",
    a: "Already paying? Your price does not change and your plan gets more. Nobody pays more.",
  },
  {
    q: "What are Liquidity and Squeeze?",
    a: (
      <>
        Two new layers on the chart. Liquidity marks the levels the market keeps returning to and weighs each one - how close, how often tested, how fresh. Squeeze marks the periods when volatility contracts and the market goes quiet before it moves, and the moment they end. Both are in Vector and Vantage. {" "}
        <Link href="/manual#liquidity" className="text-[#33CDD3] underline underline-offset-2 transition-colors hover:text-white">
          Read the layer descriptions →
        </Link>
      </>
    ),
  },
  {
    q: "Is TrendSpider included in the plan?",
    a: (
      <>
        Structura is compatible with TrendSpider - the indicators and the Sentinel scanner run natively on the platform. TrendSpider itself is a separate product with its own subscription, purchased directly from TrendSpider; Structura membership does not include it. Prism is independent of the platform: it runs in the Structura web app. {" "}
        <a
          href="https://trendspider.com/trading-tools-store/collection/trendgo-trading-system/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#33CDD3] underline underline-offset-2 hover:text-white transition-colors"
        >
          Get Structura tools for TrendSpider →
        </a>
      </>
    ),
  },
  {
    q: "Is annual billing available?",
    a: "Yes. Annual is ten months for twelve: Compass $290, Vector $890, Vantage $1,990 a year. Switch the toggle above the plans to see annual prices.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes. All paid plans include a 14-day free trial. No credit card is required to start the trial.",
  },
  {
    q: "What payment methods are accepted?",
    a: "Major credit and debit cards are accepted through PayPro Global, our checkout provider. Crypto payments are not currently supported.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel at any time and keep access through the end of your current billing period. No cancellation fees.",
  },
  {
    q: "How does the refund policy work?",
    a: "Refund requests are handled on a case-by-case basis. Contact us at contact@structuramarkets.com and we will review your situation within 1-2 business days.",
  },
];

// Title management is handled by the SEO component (react-helmet-async)

export function StructuraLogo({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <img
        src="/brand/structura-icon-512.png"
        alt="Structura"
        className="h-10 w-10 object-contain"
        width={40}
        height={40}
      />
    );
  }
  return (
    <div className="flex items-center gap-3">
      <img
        src="/brand/structura-icon-512.png"
        alt=""
        className="h-11 w-11 object-contain sm:h-12 sm:w-12"
        width={48}
        height={48}
        aria-hidden="true"
      />
      <div className="flex flex-col leading-none">
        <span
          className="text-2xl font-black text-white sm:text-[1.6rem]"
          style={{ fontFamily: "'DM Sans', 'Inter', sans-serif", letterSpacing: "-0.025em" }}
        >
          Structura
        </span>
        <span
          className="mt-0.5 hidden whitespace-nowrap text-[0.58rem] font-medium uppercase tracking-[0.12em] text-slate-500 sm:block"
        >
          Market Structure Intelligence
        </span>
      </div>
    </div>
  );
}

export function PageShell({ children, hero, hideFooterCta, showPreferredSources }: { children: ReactNode; hero?: ReactNode; hideFooterCta?: boolean; showPreferredSources?: boolean }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen [overflow-x:clip] bg-[#020617] text-slate-50 selection:bg-cyan-300/30">
      <div className="fixed inset-0 -z-10 bg-[#020617]" />
      <div className="pointer-events-none fixed inset-0 -z-10 structura-grid opacity-[0.18]" />
      <div className="pointer-events-none fixed -left-24 top-10 -z-10 h-96 w-96 rounded-full bg-cyan-400/12 blur-3xl" />
      <div className="pointer-events-none fixed -right-24 top-1/3 -z-10 h-[32rem] w-[32rem] rounded-full bg-emerald-400/10 blur-3xl" />

      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#050814]/76 backdrop-blur-2xl" style={{top: 'var(--banner-h, 0px)'}}>
        <div className="container flex h-20 items-center justify-between gap-4">
          <Link
            href="/"
            className="transition hover:opacity-90"
            onClick={() => setMobileOpen(false)}
          >
            <StructuraLogo />
          </Link>
          <nav className="hidden items-center gap-5 text-sm font-semibold text-slate-300 lg:flex">
            {navItems.map(item => (
              <Link
                key={item.href}
                href={item.href}
                className="whitespace-nowrap rounded-full px-2 py-2 transition hover:bg-white/[0.04] hover:text-white"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <Button
              className="btn-glow rounded-full bg-[#33CDD3] px-6 font-bold text-[#061018] hover:bg-[#69e5e9]"
              asChild
            >
              <Link href="/free-tools#signup">Signup for Free Access</Link>
            </Button>
          </div>
          <button
            className="rounded-full border border-white/10 bg-white/[0.04] p-3 text-white lg:hidden"
            onClick={() => setMobileOpen(value => !value)}
            aria-label="Toggle navigation"
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
        {mobileOpen && (
          <div className="border-t border-white/10 bg-[#050814]/95 px-5 py-5 backdrop-blur-2xl lg:hidden">
            <div className="container grid gap-2">
              {navItems.map(item => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-2xl px-4 py-3 text-sm font-semibold text-slate-200 transition hover:bg-white/[0.06]"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      <div style={{paddingTop: 'calc(var(--banner-h, 0px) + 5rem)'}}>{hero}</div>
      <main>{children}</main>
      <SiteFooter hideCtaBand={hideFooterCta} showPreferredSources={showPreferredSources} />
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  body,
  showEyebrow = false,
}: {
  eyebrow: string;
  title: string;
  body?: string;
  showEyebrow?: boolean;
}) {
  return (
    <div className="reveal mx-auto mb-12 max-w-3xl text-center">
      {showEyebrow && (
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.42em] text-[#33CDD3]">
          {eyebrow}
        </p>
      )}
      <h2 className="text-3xl font-black tracking-[-0.045em] text-white md:text-5xl">
        {title}
      </h2>
      {body && (
        <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-300 md:text-lg">
          {body}
        </p>
      )}
    </div>
  );
}

function RevealController() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(
          entry =>
            entry.isIntersecting && entry.target.classList.add("is-visible")
        ),
      { threshold: 0.12 }
    );
    const elements = document.querySelectorAll(".reveal");
    elements.forEach((element, index) => {
      if (element instanceof HTMLElement)
        element.style.transitionDelay = `${Math.min(index * 28, 190)}ms`;
      observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  return null;
}

const heroWorkflowSteps = [
  {
    key: "price",
    label: "PRICE",
    eyebrow: "Layer 01",
    title: "Clean price action",
    description:
      "The workflow starts with the raw market chart, before adding any Structura interpretation layers.",
    image: "/hero-layers/price.png",
    accent: "#94A3B8",
    marker: "Price only",
  },
  {
    key: "structure",
    label: "STRUCTURE",
    eyebrow: "Layer 02",
    title: "Accumulate reveals structure",
    description:
      "The Structure step adds the Accumulate layer so users can see where market structure begins to form.",
    image: "/hero-layers/structure.png",
    accent: "#19F6A4",
    marker: "Accumulate detected",
  },
  {
    key: "momentum",
    label: "MOMENTUM",
    eyebrow: "Layer 03",
    title: "Horizon adds momentum context",
    description:
      "Momentum introduces the Horizon panel and shows whether the structural read is supported by broader transition behavior.",
    image: "/hero-layers/momentum.png",
    accent: "#33CDD3",
    marker: "Horizon active",
  },
  {
    key: "trend",
    label: "TREND",
    eyebrow: "Layer 04",
    title: "Trend behavior confirms the phase",
    description:
      "The Trend step adds trend detection so structure and momentum can be validated against the current market behavior.",
    image: "/hero-layers/trend.png",
    accent: "#7C3AED",
    marker: "Trend confirmation",
  },
  {
    key: "expansion",
    label: "EXPANSION",
    eyebrow: "Layer 05",
    title: "Expansion completes the read",
    description:
      "The final layer shows how the workflow progresses after structure, momentum, and trend have been added to the chart.",
    image: "/sentinel-demo.png",
    accent: "#44BD75",
    marker: "Expansion phase",
  },
];

const productWorkflowSteps = [
  {
    ...heroWorkflowSteps.find(step => step.key === "structure")!,
    stepIndex: 1,
  },
  {
    ...heroWorkflowSteps.find(step => step.key === "momentum")!,
    stepIndex: 2,
  },
  {
    ...heroWorkflowSteps.find(step => step.key === "trend")!,
    stepIndex: 3,
  },
  {
    key: "sentinel",
    label: "SCAN",
    eyebrow: "Layer 05",
    title: "Sentinel scans the market context",
    description:
      "Sentinel scans selected markets and timeframes for structure, momentum, and trend behavior conditions before traders open individual charts.",
    image: "/sentinel-demo.png",
    accent: "#33CDD3",
    marker: "Sentinel active",
    stepIndex: 4,
  },
];

function HeroMockup() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeStep = heroWorkflowSteps[activeIndex];

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex(current => (current + 1) % heroWorkflowSteps.length);
    }, 3200);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="reveal relative mx-auto max-w-3xl xl:max-w-4xl">
      <div className="absolute -inset-6 rounded-[2.75rem] bg-[conic-gradient(from_180deg_at_50%_50%,rgba(51,205,211,0.36),rgba(25,246,164,0.22),rgba(124,58,237,0.20),rgba(51,205,211,0.36))] opacity-70 blur-2xl" />
      <div className="hero-terminal relative overflow-hidden rounded-[2.15rem] border border-cyan-300/20 bg-[#07101d]/92 p-3 shadow-[0_34px_140px_rgba(51,205,211,0.18)] backdrop-blur-2xl sm:p-4">
        <div className="pointer-events-none absolute inset-0 hero-terminal-sheen" />
        <div className="relative mb-3 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.045] px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-[#ff6b93] shadow-[0_0_16px_rgba(255,107,147,0.35)]" />
            <span className="h-3 w-3 rounded-full bg-[#f6d365] shadow-[0_0_16px_rgba(246,211,101,0.35)]" />
            <span className="h-3 w-3 rounded-full bg-[#44BD75] shadow-[0_0_16px_rgba(68,189,117,0.35)]" />
          </div>
          <div className="hidden items-center gap-2 text-[0.68rem] font-black uppercase tracking-[0.28em] text-cyan-100 sm:flex">
            <span
              className="h-2 w-2 animate-pulse rounded-full"
              style={{ backgroundColor: activeStep.accent }}
            />
            Structura · reading the transition
          </div>
        </div>

        <div className="relative overflow-hidden rounded-[1.65rem] border border-white/10 bg-[#060a16]">
          <div className="absolute inset-0 z-0 structura-grid opacity-[0.11]" />
          <div className="relative aspect-[1.45/1] min-h-[360px] overflow-hidden sm:aspect-[1.55/1] lg:min-h-[470px]">
            <img
              src="/hero-layers/price.png"
              alt="Raw SPY price chart without Structura overlays"
              className="absolute inset-0 h-full w-full object-cover opacity-100"
              loading="eager"
              width={988}
              height={820}
            />
            {heroWorkflowSteps.slice(1).map((step, index) => {
              const realIndex = index + 1;
              const isActive = realIndex === activeIndex;
              return (
                <img
                  key={step.key}
                  src={step.image}
                  alt={`${step.label} Structura workflow layer`}
                  className={`hero-layer-image absolute inset-0 h-full w-full object-cover object-top ${isActive ? "is-active" : ""}`}
                  loading="eager"
                  aria-hidden={!isActive}
                />
              );
            })}

            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#060a16]/75 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#060a16]/86 to-transparent" />
            <div className="pointer-events-none absolute inset-0 hero-scanline opacity-40" />

            <div className="absolute left-4 top-4 rounded-2xl border border-white/10 bg-[#07101d]/78 px-4 py-3 shadow-2xl backdrop-blur-xl sm:left-6 sm:top-6">
              <p className="text-[0.62rem] font-black uppercase tracking-[0.24em] text-slate-400">
                {activeStep.eyebrow}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full shadow-[0_0_20px_currentColor]"
                  style={{
                    backgroundColor: activeStep.accent,
                    color: activeStep.accent,
                  }}
                />
                <span className="text-sm font-black uppercase tracking-[0.18em] text-white">
                  {activeStep.label}
                </span>
              </div>
            </div>

            <div className="absolute bottom-4 left-4 max-w-[19rem] rounded-2xl border border-cyan-300/18 bg-[#07101d]/78 p-4 shadow-2xl backdrop-blur-xl sm:left-6 sm:bottom-5">
              <p
                className="text-xs font-black uppercase tracking-[0.22em]"
                style={{ color: activeStep.accent }}
              >
                {activeStep.marker}
              </p>
              <h3 className="mt-2 text-lg font-black tracking-[-0.03em] text-white sm:text-xl">
                {activeStep.title}
              </h3>
              <p className="mt-2 text-xs leading-5 text-slate-300 sm:text-sm">
                {activeStep.description}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="hero-step-dock relative z-10 mx-2 mt-5 rounded-2xl border border-cyan-300/20 bg-[#07101d]/88 p-2 shadow-[0_20px_70px_rgba(51,205,211,0.16)] backdrop-blur-xl sm:mx-4 sm:mt-6 lg:mx-8">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {heroWorkflowSteps.map((step, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-pressed={isActive}
                className={`hero-step-button rounded-xl border px-3 py-3 text-[0.68rem] font-black uppercase tracking-[0.18em] transition duration-300 sm:text-xs ${
                  isActive
                    ? "border-cyan-200/70 bg-cyan-300/14 text-white shadow-[0_0_32px_rgba(51,205,211,0.22)]"
                    : "border-cyan-300/22 bg-white/[0.035] text-slate-300 hover:border-cyan-200/60 hover:bg-cyan-300/10 hover:text-white"
                }`}
                style={
                  isActive
                    ? { boxShadow: `0 0 34px ${step.accent}33` }
                    : undefined
                }
              >
                {step.label}
              </button>
            );
          })}
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/10">
          <div
            key={activeStep.key}
            className="hero-step-progress h-full rounded-full"
            style={{
              background: `linear-gradient(90deg, ${activeStep.accent}, #33CDD3)`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

const productWorkflowDetails = [
  {
    key: "accumulate",
    stepIndex: 1,
    name: "Accumulate",
    layer: "Structure Layer",
    summary:
      "Accumulate identifies structural areas where price may be stabilizing, rebuilding, or reconnecting with its long-term market structure after a reset.",
    analyzes: [
      "price relationship to the Accumulate structure curve",
      "distance between price and structural baseline",
      "compression around the curve",
      "repeated reactions around the same structural area",
      "whether price is expanding away from or reconnecting with structure",
    ],
    shows: [
      "Accumulate zones",
      "structure curve",
      "distance from structure",
      "accumulation state",
      "institutional volume context",
    ],
    read: [
      "Price near the curve = market is reconnecting with structure.",
      "Price stabilizing around the curve = structure may be rebuilding.",
      "Price far above the curve = price may be extended from structure.",
      "Price below the curve = structure is not fully reclaimed yet.",
    ],
    important:
      "Accumulate is not a buy signal. It is the structure layer of the system.",
  },
  {
    key: "horizon",
    stepIndex: 2,
    name: "Horizon",
    layer: "Momentum Layer",
    summary:
      "Horizon reads the momentum condition behind price, helping traders understand whether the market is washed out, recovering, overheated, or weakening.",
    analyzes: [
      "momentum direction",
      "momentum intensity",
      "overbought / oversold conditions",
      "rate of momentum recovery",
      "momentum compression and expansion",
      "shifts around the zero line",
    ],
    shows: [
      "momentum histogram",
      "momentum line",
      "overbought / oversold bands",
      "recovery from weakness",
      "blue / purple confirmation bars for extreme zones",
    ],
    read: [
      "Deep green lower zone = momentum is washed out.",
      "Rising green line = momentum recovery from weakness.",
      "Red upper zone = momentum is extended / overheated.",
      "Zero-line reclaim = possible momentum regime shift.",
      "Blue vertical bars = increased downside volatility / oversold pressure.",
      "Purple vertical bars = reduced volatility / potential exhaustion around upper zones.",
    ],
    important:
      "Horizon does not predict reversals. It provides momentum context.",
  },
  {
    key: "pulse",
    stepIndex: 3,
    name: "Pulse",
    layer: "Trend Behavior Layer",
    summary:
      "Pulse detects changes in trend behavior by visualizing constructive or deteriorating market behavior directly on price.",
    analyzes: [
      "price behavior relative to recent market structure",
      "trend participation",
      "volatility-adjusted behavior",
      "constructive vs deteriorating candle behavior",
      "continuation vs weakening conditions",
    ],
    shows: [
      "green dots below candles",
      "red dots above candles",
      "areas where trend behavior starts improving",
      "areas where trend behavior starts deteriorating",
    ],
    read: [
      "Green dots under candles = constructive trend behavior.",
      "Red dots above candles = deteriorating / defensive trend behavior.",
      "Green dots appearing after Accumulate + Horizon recovery = stronger confirmation of transition.",
      "Red dots after extended move = possible trend exhaustion or distribution behavior.",
    ],
    important:
      "Pulse is not a standalone entry signal. It is a trend behavior confirmation layer.",
  },
  {
    key: "sentinel",
    stepIndex: 4,
    name: "Sentinel",
    layer: "Scanning Layer",
    summary:
      "Sentinel reads selected markets and timeframes for structural, momentum, and trend behavior conditions, helping traders decide where analysis should focus next before opening individual charts.",
    analyzeTitle: "What it reads",
    analyzes: [
      "selected markets and timeframes",
      "structural behavior conditions",
      "momentum behavior conditions",
      "trend behavior conditions",
      "watchlist-level structure and momentum states",
    ],
    shows: [
      "scanner table",
      "market condition overview",
      "timeframe context",
      "watchlist-level structure states",
      "watchlist-level momentum states",
    ],
    readTitle: "How to read it",
    read: [
      "Identify where analysis should focus next before opening individual charts.",
      "Prioritize markets that deserve a closer structure, momentum, and trend review.",
      "Use it as a market awareness layer before detailed chart work.",
    ],
    important:
      "Sentinel is not an auto-trading engine. It is a market awareness layer.",
  },
];

function WorkflowMockup({
  activeIndex,
  setActiveIndex,
  compact = false,
}: {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  compact?: boolean;
}) {
  const activeStep = compact
    ? (productWorkflowSteps.find(step => step.stepIndex === activeIndex) ??
      heroWorkflowSteps[activeIndex])
    : heroWorkflowSteps[activeIndex];

  return (
    <div
      className={`reveal relative mx-auto ${compact ? "max-w-5xl" : "max-w-3xl xl:max-w-4xl"}`}
    >
      <div className="absolute -inset-6 rounded-[2.75rem] bg-[conic-gradient(from_180deg_at_50%_50%,rgba(51,205,211,0.36),rgba(25,246,164,0.22),rgba(124,58,237,0.20),rgba(51,205,211,0.36))] opacity-70 blur-2xl" />
      <div className="hero-terminal relative overflow-hidden rounded-[2.15rem] border border-cyan-300/20 bg-[#07101d]/92 p-3 shadow-[0_34px_140px_rgba(51,205,211,0.18)] backdrop-blur-2xl sm:p-4">
        <div className="pointer-events-none absolute inset-0 hero-terminal-sheen" />
        <div className="relative mb-3 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.045] px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-[#ff6b93] shadow-[0_0_16px_rgba(255,107,147,0.35)]" />
            <span className="h-3 w-3 rounded-full bg-[#f6d365] shadow-[0_0_16px_rgba(246,211,101,0.35)]" />
            <span className="h-3 w-3 rounded-full bg-[#44BD75] shadow-[0_0_16px_rgba(68,189,117,0.35)]" />
          </div>
          <div className="hidden items-center gap-2 text-[0.68rem] font-black uppercase tracking-[0.28em] text-cyan-100 sm:flex">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: activeStep.accent }}
            />
            Structura · reading the transition
          </div>
        </div>

        <div className="relative overflow-hidden rounded-[1.65rem] border border-white/10 bg-[#060a16]">
          <div className="absolute inset-0 z-0 structura-grid opacity-[0.11]" />
          <div
            className={`relative overflow-hidden ${compact ? "aspect-[1.42/1] min-h-[380px] lg:min-h-[560px]" : "aspect-[1.45/1] min-h-[360px] sm:aspect-[1.55/1] lg:min-h-[470px]"}`}
          >
            <img
              src="/hero-layers/price.png"
              alt="Raw SPY price chart without Structura overlays"
              className="absolute inset-0 h-full w-full object-cover opacity-100"
              loading="eager"
              width={988}
              height={820}
            />
            {(compact ? productWorkflowSteps : heroWorkflowSteps.slice(1)).map((step, index) => {
              const realIndex = compact ? (step as typeof productWorkflowSteps[0]).stepIndex : index + 1;
              const isActive = realIndex === activeIndex;
              return (
                <img
                  key={step.key}
                  src={step.image}
                  alt={`${step.label} Structura workflow layer`}
                  className={`hero-layer-image absolute inset-0 h-full w-full object-cover object-top ${isActive ? "is-active" : ""}`}
                  loading="eager"
                  aria-hidden={!isActive}
                />
              );
            })}

            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#060a16]/75 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#060a16]/86 to-transparent" />
            <div className="pointer-events-none absolute inset-0 hero-scanline opacity-40" />

            <div className="absolute left-4 top-4 rounded-2xl border border-white/10 bg-[#07101d]/78 px-4 py-3 shadow-2xl backdrop-blur-xl sm:left-6 sm:top-6">
              <p className="text-[0.62rem] font-black uppercase tracking-[0.24em] text-slate-400">
                {activeStep.eyebrow}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full shadow-[0_0_20px_currentColor]"
                  style={{
                    backgroundColor: activeStep.accent,
                    color: activeStep.accent,
                  }}
                />
                <span className="text-sm font-black uppercase tracking-[0.18em] text-white">
                  {activeStep.label}
                </span>
              </div>
            </div>

            <div className="absolute bottom-4 left-4 max-w-[19rem] rounded-2xl border border-cyan-300/18 bg-[#07101d]/78 p-4 shadow-2xl backdrop-blur-xl sm:left-6 sm:bottom-5">
              <p
                className="text-xs font-black uppercase tracking-[0.22em]"
                style={{ color: activeStep.accent }}
              >
                {activeStep.marker}
              </p>
              <h3 className="mt-2 text-lg font-black tracking-[-0.03em] text-white sm:text-xl">
                {activeStep.title}
              </h3>
              <p className="mt-2 text-xs leading-5 text-slate-300 sm:text-sm">
                {activeStep.description}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="hero-step-dock relative z-10 mx-2 mt-5 rounded-2xl border border-cyan-300/20 bg-[#07101d]/88 p-2 shadow-[0_20px_70px_rgba(51,205,211,0.16)] backdrop-blur-xl sm:mx-4 sm:mt-6 lg:mx-8">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
          {productWorkflowSteps.map(step => {
            const stepIndex = step.stepIndex;
            const isActive = stepIndex === activeIndex;
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => setActiveIndex(stepIndex)}
                aria-pressed={isActive}
                className={`hero-step-button rounded-xl border px-3 py-3 text-[0.68rem] font-black uppercase tracking-[0.18em] transition duration-300 sm:text-xs ${isActive ? "border-cyan-200/70 bg-cyan-300/14 text-white shadow-[0_0_32px_rgba(51,205,211,0.22)]" : "border-cyan-300/22 bg-white/[0.035] text-slate-300 hover:border-cyan-200/60 hover:bg-cyan-300/10 hover:text-white"}`}
                style={
                  isActive
                    ? { boxShadow: `0 0 34px ${step.accent}33` }
                    : undefined
                }
              >
                {step.label}
              </button>
            );
          })}
        </div>
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/10">
          <div
            key={activeStep.key}
            className="hero-step-progress h-full rounded-full"
            style={{
              background: `linear-gradient(90deg, ${activeStep.accent}, #33CDD3)`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function ProductWorkflowSection({ showDetailPanel = false, showHeader = true }: { showDetailPanel?: boolean; showHeader?: boolean }) {
  const [activeIndex, setActiveIndex] = useState(1);
  const activeDetail = (
    productWorkflowDetails.find(detail => detail.stepIndex === activeIndex) ??
    productWorkflowDetails[0]
  ) as (typeof productWorkflowDetails)[0] & { visualOutput?: string[] };

  return (
    <section className="relative overflow-hidden bg-[#050d1a] py-16 lg:py-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(51,205,211,0.12),transparent_46%)]" />
      <div className="container relative z-10">
        {showHeader && (
          <div className="reveal mx-auto mb-12 max-w-4xl text-center">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.42em] text-[#33CDD3]">The framework</p>
            <h2 className="mx-auto text-4xl font-black leading-[0.96] tracking-[-0.06em] text-white md:text-6xl">
              One framework for reading market transitions.
            </h2>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-400 md:text-xl">
              Structura organizes market analysis into layers: structure, momentum, trend behavior and scanning - and, from this week, liquidity and volatility.
            </p>
          </div>
        )}
        <WorkflowMockup
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          compact
        />

        <div className="reveal mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {productWorkflowDetails.map(detail => {
            const isActive = detail.stepIndex === activeIndex;
            return (
              <div
                key={detail.key}
                className={`rounded border p-5 text-left opacity-100 transition duration-300 ${isActive ? "border-cyan-300/60 bg-cyan-300/[0.09] shadow-[0_0_42px_rgba(51,205,211,0.14)]" : "border-white/10 bg-white/[0.04] hover:border-cyan-300/35 hover:bg-white/[0.06]"}`}
              >
                <button
                  type="button"
                  onClick={() => setActiveIndex(detail.stepIndex)}
                  aria-pressed={isActive}
                  className="w-full text-left"
                >
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-[#33CDD3]">
                    {detail.layer}
                  </p>
                  <h3 className="mt-3 text-2xl font-black tracking-[-0.04em] text-white">
                    {detail.name}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-300">
                    {detail.summary}
                  </p>
                </button>
                <Link href={`/tools/${detail.key}`} className="mt-4 inline-flex text-xs font-bold text-[#33CDD3] transition hover:text-white">
                  Full {detail.name} page →
                </Link>
              </div>
            );
          })}
        </div>

        {showDetailPanel ? (
          <Card className="reveal mt-7 overflow-hidden rounded-[2rem] border border-cyan-300/20 bg-[#07101d]/88 p-6 shadow-[0_0_70px_rgba(51,205,211,0.10)] backdrop-blur-xl lg:p-8">
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.28em] text-[#33CDD3]">
                  Selected layer
                </p>
                <h3 className="mt-4 text-4xl font-black tracking-[-0.055em] text-white">
                  {activeDetail.name}
                </h3>
                <p className="mt-2 text-lg font-semibold text-cyan-100">
                  {activeDetail.layer}
                </p>
                <p className="mt-5 text-base leading-8 text-slate-300">
                  {activeDetail.summary}
                </p>
                <Link
                  href={`/tools/${activeDetail.key}`}
                  className="mt-5 inline-flex text-sm font-bold text-[#33CDD3] transition hover:text-white"
                >
                  Full {activeDetail.name} page →
                </Link>
                <div className="mt-6 rounded-2xl border border-amber-300/20 bg-amber-300/[0.07] p-4 text-sm leading-6 text-amber-100">
                  <strong>Important:</strong> {activeDetail.important}
                </div>
              </div>
              <div className="grid gap-5 md:grid-cols-3">
                <DetailList
                  title={activeDetail.analyzeTitle ?? "What it analyzes"}
                  items={activeDetail.analyzes}
                />
                <DetailList title="What it shows" items={activeDetail.shows} />
                <DetailList
                  title={activeDetail.readTitle ?? "How to read it"}
                  items={activeDetail.read}
                />
              </div>
            </div>
          </Card>
        ) : (
          <div className="reveal mt-8 text-center">
            <Link
              href="/product"
              className="inline-flex items-center gap-2 text-sm font-black tracking-wide text-[#33CDD3] transition hover:text-cyan-100"
            >
              Plus two new layers: Liquidity and Squeeze
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

const PRISM_HERO_LOOP = "/manus-storage/prism-loop-600_379b81e8.gif";
const PRISM_PREVIEWS = [
  {
    src: "/manus-storage/prism-interface-loop_6e038a1f.gif",
    type: "image",
    alt: "Prism interface with a market watchlist and XAUUSD question ready to submit.",
  },
  {
    src: "/manus-storage/prism_read_spy_followups_2887fed1.mp4",
    type: "video",
    poster: "/manus-storage/prism_read_spy_followups_326b509b.webp",
    alt: "Prism follow-up read for a market structure question.",
  },
  {
    src: "/manus-storage/prism_alerts_5cf4ac8b.mp4",
    type: "video",
    poster: "/manus-storage/prism_alerts_4d2da410.webp",
    alt: "Prism alerts view for market-structure readings.",
  },
  {
    src: "/manus-storage/prism_read_spy_loop_84eb700d.mp4",
    type: "video",
    poster: "/manus-storage/prism_read_spy_loop_b6ab148e.webp",
    alt: "Prism read loop showing a market structure assistant response.",
  },
] as const;

const prismReadCards = [
  {
    title: "What you ask",
    items: [
      "any symbol, any timeframe",
      "several timeframes at once",
      "your whole watchlist in one question",
      '"what changed since yesterday"',
    ],
  },
  {
    title: "What you get",
    items: [
      "the state of every layer, named",
      "where the layers agree and where they conflict",
      "the one thing blocking a cleaner read",
      "what would have to change - as conditions, not forecasts",
      "the read in the voice you know from Sundays",
    ],
  },
  {
    title: "What it will not do",
    items: [
      "no buy, no sell",
      "no targets, no stops",
      "no narrative when the layers are quiet - it says they are quiet",
    ],
  },
];

function PrismProductSection() {
  return (
    <section id="prism" className="relative overflow-hidden bg-[#020617] py-16 lg:py-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_18%,rgba(51,205,211,0.13),transparent_30%)]" />
      <div className="container relative z-10">
        <div className="grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14">
          <div className="reveal max-w-2xl">
            <p className="text-xs font-black uppercase tracking-[0.32em] text-[#33CDD3]">PRISM · AI MARKET STRUCTURE ASSISTANT</p>
            <h2 className="mt-5 text-4xl font-black leading-[0.98] tracking-[-0.055em] text-white md:text-5xl xl:text-6xl">
              Your chart shows six layers. Prism reads all of them.
            </h2>
            <p className="mt-6 text-base leading-8 text-slate-300 md:text-lg">
              Prism is an AI assistant that reads every Structura layer on your chart - and the layers we do not publish - and writes the read. Ask about any market and timeframe, or several at once. It tells you which layers agree, which disagree, which are silent, and how ready the whole system is.
            </p>
            <p className="mt-4 text-sm italic text-slate-500">Prism&apos;s output is generated by an AI system from Structura&apos;s layer data. It is educational analysis, not financial advice, and it can be wrong.</p>
          </div>

          <div className="reveal relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-cyan-300/10 blur-2xl" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-cyan-300/20 bg-[#07101d] shadow-[0_28px_90px_rgba(51,205,211,0.14)]">
              <img
                src={PRISM_HERO_LOOP}
                alt="Prism - AI market structure assistant reading XAUUSD 1D"
                className="block aspect-video h-auto w-full object-cover"
              />
            </div>
          </div>
        </div>

        <div className="reveal mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PRISM_PREVIEWS.map((preview) => (
            <div key={preview.src} className="overflow-hidden rounded-[1.5rem] border border-cyan-300/20 bg-[#050b13] shadow-[0_20px_70px_rgba(51,205,211,0.12)]">
              {preview.type === "video" ? (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  poster={preview.poster}
                  aria-label={preview.alt}
                  className="block aspect-[0.68/1] h-auto w-full object-cover"
                >
                  <source src={preview.src} type="video/mp4" />
                </video>
              ) : (
                <img src={preview.src} alt={preview.alt} className="block aspect-[0.68/1] h-auto w-full object-cover" loading="lazy" />
              )}
              </div>
          ))}
        </div>

        <div className="reveal mt-10 grid gap-4 md:grid-cols-3">
          {prismReadCards.map(card => (
            <div key={card.title} className="rounded-[1.25rem] border border-white/10 bg-white/[0.035] p-5">
              <h3 className="text-sm font-black uppercase tracking-[0.18em] text-white">{card.title}</h3>
              <div className="mt-4 grid gap-3">
                {card.items.map(item => (
                  <p key={item} className="flex gap-2 text-sm leading-6 text-slate-300">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-[#33CDD3]" />
                    {item}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="reveal mt-6 rounded-2xl border border-amber-300/20 bg-amber-300/[0.07] p-5 text-sm leading-7 text-amber-100">
          <strong>Important:</strong> Prism reads layers you cannot see on a screenshot. That is why a chart pasted into a chatbot is not the same thing - and why Prism is a product, not a prompt.
        </div>
        <p className="reveal mt-4 text-sm leading-7 text-slate-400">
          Readiness is how much of the system agrees, and how strongly. It is not a probability and it is not a forecast.
        </p>
        <div className="reveal mt-7 flex flex-wrap gap-4">
          <Button className="rounded-full bg-[#33CDD3] px-6 font-black text-[#061018] hover:bg-[#69e5e9]" asChild>
            <Link href="/education/meet-prism">See it work <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button variant="outline" className="rounded-full border-cyan-300/45 bg-cyan-300/[0.05] px-6 font-black text-cyan-100 hover:border-cyan-200/70 hover:bg-cyan-300/[0.12] hover:text-white" asChild>
            <Link href="/pricing">Review plans</Link>
          </Button>
        </div>
        <p className="reveal mt-4 text-xs text-slate-500">Prism is included in Vector and Vantage.</p>
      </div>
    </section>
  );
}

function PrismHomeReadSection() {
  return (
    <section className="relative overflow-hidden bg-[#020617] py-16 lg:py-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_62%,rgba(51,205,211,0.12),transparent_30%)]" />
      <div className="container relative z-10">
        <div className="reveal mx-auto max-w-4xl text-center">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.42em] text-[#33CDD3]">The read</p>
          <h2 className="mx-auto text-4xl font-black leading-[0.96] tracking-[-0.06em] text-white md:text-6xl">
            Your chart shows six layers. Prism reads all of them.
          </h2>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-400 md:text-xl">
            Prism is an AI assistant that reads every Structura layer on your chart - and the layers we do not publish - and writes the read. Ask about any market and timeframe, or several at once. It tells you which layers agree, which disagree, which are silent, and how ready the whole system is.
          </p>
        </div>

        <div className="reveal mx-auto mt-12 grid max-w-5xl items-center gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <div className="mx-auto w-full max-w-[25rem] overflow-hidden rounded-[1.75rem] border border-cyan-300/20 bg-[#050b13] shadow-[0_28px_90px_rgba(51,205,211,0.16)]">
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              poster="/manus-storage/prism_read_spy_loop_b6ab148e.webp"
              aria-label="Prism reading SPY across the 1-hour and 4-hour timeframes"
              className="block h-auto w-full"
            >
              <source src="/manus-storage/prism_read_spy_loop_84eb700d.mp4" type="video/mp4" />
            </video>
          </div>

          <div className="mx-auto w-full max-w-xl">
            <div className="space-y-5 text-left">
              {[
                "Any market, any timeframe - or several at once",
                "It names what agrees and what blocks a cleaner read",
                "Never buy, never sell",
              ].map(line => (
                <p key={line} className="flex items-start gap-4 text-lg font-semibold leading-8 text-slate-200 md:text-xl">
                  <Check className="mt-1.5 h-5 w-5 shrink-0 text-[#33CDD3]" />
                  {line}
                </p>
              ))}
            </div>
            <Button className="mt-9 rounded-full bg-[#33CDD3] px-7 font-black text-[#061018] hover:bg-[#69e5e9]" asChild>
              <Link href="/product#prism" className="inline-flex items-center gap-2">
                See Prism read a chart
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingPrismSection() {
  return (
    <section className="relative overflow-hidden bg-[#020617] py-16 lg:py-20">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(51,205,211,0.12),transparent_34%)]" />
      <div className="container relative z-10">
        <div className="reveal overflow-hidden rounded-[2rem] border border-cyan-300/20 bg-white/[0.035] p-6 shadow-[0_30px_100px_rgba(2,6,23,0.42)] lg:p-9">
          <div className="grid items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.28em] text-[#33CDD3]">PRISM · AI MARKET STRUCTURE ASSISTANT</p>
              <h2 className="mt-4 max-w-2xl text-3xl font-black leading-[1.02] tracking-[-0.05em] text-white md:text-5xl">
                Your chart shows six layers. Prism reads all of them.
              </h2>
              <div className="mt-6 max-w-2xl space-y-4 text-base leading-8 text-slate-300">
                <p>
                  Prism is an AI assistant that reads every Structura layer on your chart - and the layers we do not publish - and writes the read. Ask about any market and timeframe, or several at once. It tells you which layers agree, which disagree, which are silent, and how ready the whole system is.
                </p>
                <p>
                  Every read is dated, names the layers you can see on your chart, and says what would change it. It does not tell you what action to take. Vector includes 100 reads a month, Vantage 150.
                </p>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button className="rounded-full bg-[#33CDD3] px-6 font-black text-[#061018] hover:bg-[#69e5e9]" asChild>
                  <Link href="/education/meet-prism">See it work <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button variant="outline" className="rounded-full border-cyan-300/45 bg-cyan-300/[0.05] px-6 font-black text-cyan-100 hover:border-cyan-200/70 hover:bg-cyan-300/[0.12] hover:text-white" asChild>
                  <Link href="/manual#prism">How Prism reads</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-cyan-300/10 blur-2xl" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[1.5rem] border border-cyan-300/20 bg-[#07101d]">
                  <img
                  src={PRISM_HERO_LOOP}
                  alt="Prism - AI market structure assistant reading XAUUSD 1D"
                  className="block h-auto w-full"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const moreLayerDetails = [
  {
    key: "liquidity",
    cardKicker: "Liquidity layer",
    name: "Liquidity",
    layer: "Liquidity Layer",
    image: "/manus-storage/pasted_file_cwtdMz_image_dab29f05.png",
    imageAlt: "Gold daily chart showing Structura Liquidity support and resistance zones.",
    cardSummary:
      "Liquidity marks the levels the market keeps returning to, tracks how each one behaves, and removes it once price trades through - so the chart shows what has been respected, not everything that ever happened.",
    summary:
      "Liquidity identifies support and resistance zones from confirmed swing points, sizes each zone to current volatility, merges revisits into a single zone with a touch count, and removes zones once price closes through them. Only the nearest zones on each side of price are shown.",
    important: "Liquidity is not a level to trade from. It is the map of where the market has kept coming back.",
    analyzes: [
      "confirmed swing highs and lows",
      "zone width relative to current volatility",
      "revisits of the same area, counted as touches",
      "whether price has closed through a zone",
      "distance between price and the nearest zone on each side",
    ],
    shows: [
      "support zones (green) and resistance zones (red)",
      "nearest zones only, extended right while they hold",
      "on-chart count and tracked count",
      "nearest support and resistance prices",
    ],
    read: [
      "Price at a green zone = at a level that has held before.",
      "Price at a red zone = at a level that has capped price before.",
      "A zone disappearing = price closed through it; it is no longer liquidity.",
      "A zone appearing = a swing confirmed a few bars ago, never on the current bar.",
    ],
  },
  {
    key: "squeeze",
    cardKicker: "Volatility layer",
    name: "Squeeze",
    layer: "Volatility Layer",
    image: "/manus-storage/pasted_file_CfTgPr_image_7b0f1122.png",
    imageAlt: "Gold daily chart with the Structura Squeeze volatility and momentum pane.",
    cardSummary:
      "Squeeze marks the periods when volatility contracts and the market goes quiet before it moves - and the moment they end - while reading whether momentum is building, slowing, falling, or recovering.",
    summary:
      "Squeeze identifies compression - one volatility envelope contracting fully inside another - marks the release, and reads momentum in four states through both phases. It lives in its own pane: a momentum histogram, state dots on the zero line, and a background tint during every compression.",
    important: "Squeeze is not a breakout signal. It is the volatility layer of the system.",
    analyzes: [
      "whether volatility is compressed, released, or neither",
      "momentum direction and whether it is building or fading",
      "the transition through the zero line",
      "how long the current compression has lasted",
    ],
    shows: [
      "momentum histogram in four colors",
      "state dots: compression on, released, in between",
      "background tint across every compression phase",
      "panel with state and momentum direction",
    ],
    read: [
      "Red dots and tint = compression on; the market is quiet.",
      "Green dots = compression just released; volatility is expanding.",
      "Grey dots = neither; do not read it as a squeeze.",
      "Histogram color = momentum regime, not a trade instruction.",
    ],
  },
];

function MoreLayersSection() {
  const [activeKey, setActiveKey] = useState("liquidity");
  const activeDetail = moreLayerDetails.find(detail => detail.key === activeKey) ?? moreLayerDetails[0];

  return (
    <section className="relative overflow-hidden bg-[#020617] py-16 lg:py-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_50%,rgba(68,189,117,0.10),transparent_28%)]" />
      <div className="container relative z-10">
        <SectionHeader
          eyebrow="More layers"
          showEyebrow
          title="Two more questions, two more layers."
          body="Liquidity and Squeeze read the chart alongside the four above. Included in Vector and Vantage."
        />

        <div className="reveal grid gap-4 md:grid-cols-2" role="tablist" aria-label="Additional Structura layers">
          {moreLayerDetails.map(detail => {
            const isActive = activeKey === detail.key;
            return (
              <div
                key={detail.key}
                className={`rounded border p-6 text-left transition duration-300 ${isActive ? "border-cyan-300/60 bg-cyan-300/[0.09] shadow-[0_0_42px_rgba(51,205,211,0.14)]" : "border-white/10 bg-white/[0.04] hover:border-cyan-300/35 hover:bg-white/[0.06]"}`}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveKey(detail.key)}
                  className="w-full text-left"
                >
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-[#33CDD3]">{detail.cardKicker}</p>
                  <h3 className="mt-3 text-3xl font-black tracking-[-0.04em] text-white">{detail.name}</h3>
                  <p className="mt-4 text-sm leading-7 text-slate-300">{detail.cardSummary}</p>
                  <div className="mt-5 overflow-hidden rounded-xl border border-white/10 bg-[#07101d]">
                    <img src={detail.image} alt={detail.imageAlt} className="aspect-[1.96/1] w-full object-cover" loading="eager" />
                  </div>
                </button>
                <Link href={`/tools/${detail.key}`} className="mt-5 inline-flex text-sm font-bold text-[#33CDD3] transition hover:text-white">
                  Full {detail.name} page →
                </Link>
              </div>
            );
          })}
        </div>

        <Card className="reveal mt-7 overflow-hidden rounded-[2rem] border border-cyan-300/20 bg-[#07101d]/88 p-6 shadow-[0_0_70px_rgba(51,205,211,0.10)] backdrop-blur-xl lg:p-8" role="tabpanel">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.28em] text-[#33CDD3]">Selected layer</p>
              <h3 className="mt-4 text-4xl font-black tracking-[-0.055em] text-white">{activeDetail.name}</h3>
              <p className="mt-2 text-lg font-semibold text-cyan-100">{activeDetail.layer}</p>
              <p className="mt-5 text-base leading-8 text-slate-300">{activeDetail.summary}</p>
              <Link
                href={`/tools/${activeDetail.key}`}
                className="mt-5 inline-flex text-sm font-bold text-[#33CDD3] transition hover:text-white"
              >
                Full {activeDetail.name} page →
              </Link>
              <div className="mt-6 rounded-2xl border border-amber-300/20 bg-amber-300/[0.07] p-4 text-sm leading-6 text-amber-100">
                <strong>Important:</strong> {activeDetail.important}
              </div>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              <DetailList title="What it analyzes" items={activeDetail.analyzes} />
              <DetailList title="What it shows" items={activeDetail.shows} />
              <DetailList title="How to read it" items={activeDetail.read} />
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}

function DetailList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-[1.25rem] border border-white/10 bg-white/[0.035] p-5">
      <h4 className="text-sm font-black uppercase tracking-[0.18em] text-white">
        {title}
      </h4>
      <div className="mt-4 grid gap-3">
        {items.map(item => (
          <p key={item} className="flex gap-2 text-sm leading-6 text-slate-300">
            <Check className="mt-1 h-4 w-4 shrink-0 text-[#33CDD3]" />
            {item}
          </p>
        ))}
      </div>
    </div>
  );
}

function TechnicalSpecificationsSection() {
  const specs = [
    {
      title: "Accumulation detection",
      graphic: "image",
      image: "/accumulate-demo.png",
      body: "Accumulate surfaces zones where the market may be rebuilding balance before the next phase becomes visible on the chart.",
      support: "Structure layer - no buy/sell signals.",
    },
    {
      title: "Live structure workflow",
      graphic: "image",
      image: "/system-reference/full-system-momentum.png",
      body: "The live workflow panel shows all six layers in real time - Structure, Volatility, Momentum, Trend, Money Flow, Expansion - with percentage readings.",
      support: "Always-on context for every chart you open.",
    },
    {
      title: "Structure zone",
      graphic: "image",
      image: "/hero-layers/structure.png",
      body: "Structure zones mark where the market is in a preparation phase before expansion, with bullish and bearish context shown directly on the chart.",
      support: "Preparation before expansion context.",
    },
    {
      title: "Market screener",
      graphic: "image",
      image: "/sentinel-demo.png",
      body: "Sentinel scans multiple markets and timeframes simultaneously - showing Symbol, TF, Status, Momentum, Trend, Money Flow, and Breakout in one table.",
      support: "Reduce manual scanning time across your watchlist.",
    },
    {
      title: "Pulse trend detection",
      graphic: "image",
      image: "/pulse-demo.png",
      body: "Pulse colors each candle to reflect trend behavior phase - red for distribution, green for accumulation - making directional shifts immediately readable.",
      support: "Trend behavior layer - phase confirmation.",
    },
    {
      title: "Horizon momentum detection",
      graphic: "image",
      image: "/horizon-demo.png",
      body: "Horizon organizes the momentum read into a histogram so users can assess whether the structural shift is supported by broader transition behavior.",
      support: "Momentum layer - phase shift and overextension filter.",
    },
  ];

  const renderGraphic = (spec: any, index: number) => {
    if (spec.graphic === "platforms") {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-6 bg-slate-950 px-8 py-10">
          <div className="flex w-full max-w-[220px] items-center justify-center rounded-xl border border-white/10 bg-slate-900 px-6 py-5">
            <img
              src="/platforms/tradingview-logo.webp"
              alt="TradingView"
              className="h-7 w-auto object-contain"
              loading="lazy"
              width={120}
              height={16}
            />
          </div>
          <div className="flex w-full max-w-[220px] items-center justify-center rounded-xl border border-white/10 bg-slate-900 px-6 py-5">
            <img
              src="/platforms/trendspider-logo.webp"
              alt="TrendSpider"
              className="h-7 w-auto object-contain"
              loading="lazy"
              width={120}
              height={16}
            />
          </div>
        </div>
      );
    }

    if (spec.graphic === "annotated") {
      const callouts = [
        "Structure curve",
        "Status panel",
        "Momentum histogram",
        "Pulse dots",
      ];
      return (
        <div className="relative h-full overflow-hidden bg-slate-950">
          <img
            src={spec.image}
            alt={`${spec.title} annotated full-system visual`}
            className="h-full w-full object-cover opacity-80 transition duration-700 group-hover:scale-[1.04] group-hover:opacity-95"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent" />
          <div className="absolute inset-x-4 bottom-4 grid gap-2 sm:grid-cols-2">
            {callouts.map(callout => (
              <p
                key={callout}
                className="rounded-full border border-cyan-300/20 bg-slate-950/75 px-3 py-1.5 text-[0.62rem] font-black uppercase tracking-[0.16em] text-cyan-100 shadow-lg shadow-cyan-950/20"
              >
                {callout}
              </p>
            ))}
          </div>
        </div>
      );
    }

    if (spec.graphic === "workflow") {
      const steps = ["Reset", "Recovery", "Confirmation"];
      return (
        <div className="relative flex h-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_15%,rgba(51,205,211,0.20),transparent_32%),linear-gradient(135deg,#020617,#061827_55%,#020617)] p-6">
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          <div className="relative grid w-full gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-center">
            {steps.map((step, stepIndex) => (
              <div key={step} className="contents">
                <div className="rounded-2xl border border-white/10 bg-white/[0.075] p-4 text-center shadow-2xl shadow-cyan-950/20">
                  <p className="text-[0.62rem] font-black uppercase tracking-[0.22em] text-cyan-300">
                    Step 0{stepIndex + 1}
                  </p>
                  <p className="mt-2 text-lg font-black tracking-[-0.04em] text-white">
                    {step}
                  </p>
                </div>
                {stepIndex < steps.length - 1 ? (
                  <ArrowRight className="hidden h-5 w-5 text-cyan-300 md:block" />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (spec.graphic === "compliance") {
      return (
        <div className="relative flex h-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_20%,rgba(51,205,211,0.18),transparent_34%),linear-gradient(135deg,#020617,#07111f_48%,#020617)] p-6">
          <div className="absolute inset-x-8 top-8 h-px bg-gradient-to-r from-transparent via-cyan-300/30 to-transparent" />
          <div className="relative grid w-full gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
            <div className="rounded-3xl border border-white/10 bg-white/[0.065] p-5">
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-cyan-300" />
                <div>
                  <p className="text-[0.62rem] font-black uppercase tracking-[0.22em] text-cyan-300">
                    Decision support
                  </p>
                  <p className="mt-1 text-base font-black text-white">
                    Conditions
                  </p>
                </div>
              </div>
            </div>
            <ArrowRight className="mx-auto h-5 w-5 text-cyan-300" />
            <div className="rounded-3xl border border-white/10 bg-white/[0.065] p-5">
              <p className="text-[0.62rem] font-black uppercase tracking-[0.22em] text-cyan-300">
                Human control
              </p>
              <p className="mt-1 text-base font-black text-white">
                Your decision
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-950 p-4">
        <img
          src={spec.image}
          alt={`${spec.title} visual reference`}
          className="max-h-full max-w-full object-contain opacity-90 transition duration-700 group-hover:scale-[1.03] group-hover:opacity-100"
          loading="lazy"
        />
      </div>
    );
  };

  return (
    <section className="bg-[#020617] py-16 lg:py-24">
      <div className="container">
        <SectionHeader
          eyebrow="System Reference"
          title="Capabilities, outputs, and limits in one place."
          body="A compact reference for the markets, layers, outputs, use cases, and boundaries of the Structura visual system."
        />
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {specs.map((spec, index) => (
            <Card
              key={spec.title}
              className="reveal group overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/[0.045] transition duration-500 hover:-translate-y-1 hover:border-cyan-300/30"
            >
              <div
                className="relative overflow-hidden bg-slate-950 aspect-[4/3]"
              >
                {renderGraphic(spec, index)}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/10 to-transparent" />
                <p className="absolute left-5 top-5 rounded-full border border-cyan-300/20 bg-slate-950/75 px-3 py-1 text-xs font-black uppercase tracking-[0.24em] text-[#33CDD3]">
                  0{index + 1}
                </p>
              </div>
              <div className="p-5 md:p-6">
                <h3 className="text-xl font-black tracking-[-0.035em] text-white">
                  {spec.title}
                </h3>
                {spec.body ? (
                  <p className="mt-4 text-sm leading-6 text-slate-300">
                    {spec.body}
                  </p>
                ) : null}
                {(spec as any).items ? (
                  <div
                    className={`mt-4 grid gap-2 ${index === 6 ? "md:grid-cols-2 xl:grid-cols-3" : ""}`}
                  >
                    {(spec as any).items.map((item: string) => (
                      <p
                        key={item}
                        className="flex gap-2 text-sm leading-6 text-slate-300"
                      >
                        <Check className="mt-1 h-4 w-4 shrink-0 text-[#33CDD3]" />
                        {item}
                      </p>
                    ))}
                  </div>
                ) : null}
                {spec.support ? (
                  <p className="mt-4 border-t border-white/10 pt-4 text-xs font-semibold leading-5 text-slate-400">
                    {spec.support}
                  </p>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SiteFooter({ hideCtaBand, showPreferredSources }: { hideCtaBand?: boolean; showPreferredSources?: boolean } = {}) {
  return (
    <footer className="bg-[#020617]">
      {/* ── CTA band ── */}
      {!hideCtaBand && (
      <div className="border-y border-white/8 bg-gradient-to-r from-cyan-950/30 via-slate-900/20 to-blue-950/30">
        <div className="container py-14 text-left">
          <a
            href="/pricing"
            className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-cyan-400 transition hover:text-cyan-200"
          >
            Get Started Free →
          </a>
          <h3 className="mt-2 text-2xl font-black text-white" style={{ letterSpacing: "-0.02em" }}>
            Read structure before the move becomes obvious.
          </h3>
        </div>
      </div>
      )}

      {/* ── Main grid ── */}
      <div className="container grid gap-12 py-14 lg:grid-cols-[1.7fr_0.85fr_1.35fr_0.85fr_1.35fr]">
        {/* Brand column */}
        <div>
          <StructuraLogo />
          <p className="mt-5 max-w-xs text-sm leading-7 text-slate-400">
            Structura organizes market analysis into one repeatable workflow - Structure, Momentum, Trend, and Scan - built for traders who want clarity before the move.
          </p>
          <p className="mt-5 text-sm text-slate-500">
            Contact:{" "}
            <a href="mailto:contact@structuramarkets.com" className="text-cyan-400 transition hover:text-cyan-200">
              contact@structuramarkets.com
            </a>
          </p>
          {/* Platform badges */}
          <div className="mt-6 flex items-center gap-4">
            <span className="text-[0.65rem] font-semibold uppercase tracking-widest text-slate-600">Available on</span>
            <a href="/pricing" className="opacity-50 transition hover:opacity-100">
              <img src="/platforms/tradingview-logo.webp" alt="TradingView" className="h-4 object-contain"
              loading="lazy"
              width={120}
              height={16}
            />
            </a>
            <a href="https://trendspider.com/trading-tools-store/collection/trendgo-trading-system/" className="opacity-50 transition hover:opacity-100">
              <img src="/platforms/trendspider-logo.webp" alt="TrendSpider" className="h-4 object-contain"
              loading="lazy"
              width={120}
              height={16}
            />
            </a>
          </div>
          {showPreferredSources ? (
            <Link
              href="/read/when-the-layers-disagree"
              className="mt-6 inline-flex text-xs font-semibold text-slate-400 transition hover:text-white"
            >
              Featured in VTAD-News · No. 45 · September 2026
            </Link>
          ) : null}
          {showPreferredSources ? (
            <GooglePreferredSourcesButton compact className="mt-7 opacity-80 transition hover:opacity-100" />
          ) : null}
        </div>

        {/* Product column */}
        <div>
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-slate-500">Product</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            {[{label:"Home",href:"/"},{label:"Product",href:"/product"},{label:"Tools",href:"/tools"},{label:"Free Tools",href:"/free-tools"},{label:"Pricing",href:"/pricing"},{label:"Reviews",href:"/testimonials"},{label:"Education",href:"/education"},{label:"Manual",href:"/manual"}].map(item => (
              <li key={item.href}>
                <Link href={item.href} className="text-slate-400 transition hover:text-white">{item.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Read column */}
        <div>
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-slate-500">The Sunday Read</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            {footerReadLinks.map(item => (
              <li key={item.href}>
                <Link href={item.href} className="line-clamp-2 leading-5 text-slate-400 transition hover:text-white">{item.label}</Link>
              </li>
            ))}
          </ul>
          <Link href="/read" className="mt-4 inline-flex text-xs font-bold uppercase tracking-[0.15em] text-[#33CDD3] transition hover:text-white">View archive →</Link>
        </div>

        {/* Company column */}
        <div>
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-slate-500">Company</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            {[{label:"About",href:"/about"},{label:"Our story",href:"/trendgo-is-now-structura"},{label:"Support",href:"/support"},{label:"Contact",href:"/contact"},{label:"Terms",href:"/terms"},{label:"Privacy",href:"/privacy"}].map(item => (
              <li key={item.href}>
                <Link href={item.href} className="text-slate-400 transition hover:text-white">{item.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Disclaimer column */}
        <div>
          <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-slate-500">Risk disclaimer</p>
          <p className="mt-4 text-xs leading-6 text-slate-500">
            Structura provides analytical tools and educational market examples. It is not financial advice. Trading and investing involve risk, and historical results do not guarantee future outcomes.
          </p>
          <p className="mt-4 text-xs leading-6 text-slate-600">
            Operated by TrendGo LLC, a New York limited liability company.
          </p>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-white/8">
        <div className="container flex flex-col items-center justify-between gap-3 py-5 text-xs text-slate-600 sm:flex-row">
          <span>© {new Date().getFullYear()} TrendGo LLC · Structura</span>
          <span className="hidden sm:block">Structure → Momentum → Trend → Scan</span>
          <div className="flex items-center gap-4">
            <Link href="/terms" className="hover:text-slate-400">Terms</Link>
            <Link href="/privacy" className="hover:text-slate-400">Privacy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function ToolCard({ tool, large = false }: { tool: Tool; large?: boolean }) {
  const Icon = tool.icon;
  return (
    <Card
      className={`reveal group overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/[0.045] backdrop-blur-xl transition duration-500 hover:-translate-y-1 hover:border-cyan-300/30 hover:bg-white/[0.065] ${large ? "grid lg:grid-cols-[0.92fr_1.08fr]" : ""}`}
    >
      <div className="p-6 md:p-7">
        <div className="flex items-center justify-between gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-cyan-300/20 bg-cyan-300/10 text-[#33CDD3]">
            <Icon className="h-6 w-6" />
          </span>
          <Badge className="border-white/10 bg-white/[0.05] text-slate-200">
            {tool.layer}
          </Badge>
        </div>
        <h3 className="mt-6 text-3xl font-black tracking-[-0.04em] text-white">
          {tool.name}
        </h3>
        <p className="mt-3 text-base font-semibold leading-7 text-cyan-100">
          {tool.promise}
        </p>
        <p className="mt-4 text-sm leading-7 text-slate-300">
          {tool.description}
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {tool.technical.map(item => (
            <span
              key={item}
              className="rounded-full border border-white/10 bg-slate-950/70 px-3 py-1.5 text-xs font-semibold text-slate-300"
            >
              {item}
            </span>
          ))}
        </div>
      </div>
      <div className="relative min-h-60 overflow-hidden border-t border-white/10 bg-slate-950 lg:border-l lg:border-t-0">
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-80" />
        <img
          src={tool.image}
          alt={`${tool.name} product screenshot`}
          className="h-full min-h-60 w-full object-cover opacity-80 transition duration-700 group-hover:scale-[1.035] group-hover:opacity-100"
          loading="lazy"
        />
      </div>
    </Card>
  );
}

function PlatformSection() {
  const platforms = [
    {
      title: "TradingView",
      logo: "/platforms/tradingview-logo.webp",
      copy: "Structura is available on TradingView. Access the full workflow where you already analyze charts.",
      href: "/pricing",
    },
    {
      title: "TrendSpider",
      logo: "/platforms/trendspider-logo.webp",
      copy: "Structura is available on TrendSpider. Get the system on the platform built for systematic traders.",
      href: "https://trendspider.com/trading-tools-store/collection/trendgo-trading-system/",
    },
  ];

  return (
    <section className="bg-[#050d1a] py-16 lg:py-20">
      <div className="container">
        <SectionHeader
          eyebrow="Available on"
          title="Works where you already trade."
          body="Structura is available on TradingView and TrendSpider. No new platform to learn."
          showEyebrow
        />
        <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
          {platforms.map(platform => {
            const isExternal = platform.href.startsWith("http");
            return (
              <a
                key={platform.title}
                href={platform.href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                className="reveal group block rounded border border-white/10 bg-white/[0.045] p-7 text-center transition duration-300 hover:border-cyan-300/30 hover:bg-cyan-300/[0.055]"
              >
                <div className="mx-auto flex h-20 items-center justify-center rounded border border-white/10 bg-[#0d1829] p-4">
                  <img
                    src={platform.logo}
                    alt={`${platform.title} logo`}
                    className="max-h-12 max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <h3 className="mt-6 text-xl font-black text-white">
                  {platform.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  {platform.copy}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-cyan-300 transition group-hover:gap-3">
                  {isExternal ? "Get on TrendSpider" : "View plans"}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FrameworkSection() {
  const framework = [
    {
      step: "1",
      name: "Accumulate",
      layer: "Structure layer",
      copy: "Where price may be rebuilding.",
    },
    {
      step: "2",
      name: "Horizon",
      layer: "Momentum layer",
      copy: "Whether momentum is washed out, recovering, overheated, or weakening.",
    },
    {
      step: "3",
      name: "Pulse",
      layer: "Trend behavior layer",
      copy: "Whether behavior confirms the transition.",
    },
    {
      step: "4",
      name: "Sentinel",
      layer: "Scanning layer",
      copy: "Where to focus next across markets.",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-[#050914] py-16 lg:py-24">
      {/* Animated dot-grid background */}
      <div className="pointer-events-none absolute inset-0 [background-image:radial-gradient(rgba(51,205,211,0.12)_1px,transparent_1px)] [background-size:32px_32px]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#050914] to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050914] to-transparent" />
      <div className="container relative z-10">
        <div className="reveal max-w-4xl">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.42em] text-[#33CDD3]">The framework</p>
          <h2 className="max-w-4xl text-4xl font-black leading-[0.96] tracking-[-0.06em] text-white md:text-6xl">
            One framework for reading market transitions.
          </h2>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-400 md:text-xl">
            Structura organizes market analysis into four layers: structure,
            momentum, trend behavior, and scanning.
          </p>
        </div>

        <div className="relative mt-12 grid gap-5 lg:grid-cols-4">
          {/* Static connector line with animated pulse */}
          <div className="pointer-events-none absolute left-[7%] right-[7%] top-[2.625rem] hidden h-px bg-white/[0.07] lg:block">
            <div
              className="absolute top-0 h-full w-[18%] bg-gradient-to-r from-transparent via-[#33CDD3] to-transparent"
              style={{ animation: "frameworkPulse 3.2s cubic-bezier(0.4,0,0.6,1) infinite" }}
            />
          </div>
          {framework.map(item => (
            <div
              key={item.name}
              className="reveal relative cursor-default overflow-hidden border border-white/[0.09] bg-[#080f1e] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-colors duration-300 hover:border-cyan-300/20 hover:bg-[#0a1424]"
              style={{ borderRadius: "2px" }}
            >
              {/* Corner accent */}
              <div className="absolute left-0 top-0 h-6 w-6 border-l border-t border-[#33CDD3]/30" />
              <div className="absolute bottom-0 right-0 h-6 w-6 border-b border-r border-[#33CDD3]/10" />
              <div className="flex h-9 w-9 items-center justify-center border border-[#33CDD3]/30 bg-[#33CDD3]/[0.08] text-xs font-black tracking-widest text-[#33CDD3]">
                {item.step}
              </div>
              <h3 className="mt-6 text-2xl font-black tracking-[-0.04em] text-white">
                {item.name}
              </h3>
              <p className="mt-3 text-[0.65rem] font-bold uppercase tracking-[0.32em] text-[#33CDD3]/70">
                {item.layer}
              </p>
              <p className="mt-5 min-h-14 text-sm leading-7 text-slate-500">
                {item.copy}
              </p>
            </div>
          ))}
        </div>

        <div className="reveal mx-auto mt-9 max-w-xl border-t border-cyan-300/10 pt-5 text-center font-mono text-sm leading-7">
          <p className="font-bold tracking-[0.14em] text-[#33CDD3]">
            Accumulate → Horizon → Pulse → Sentinel
          </p>
          <p className="text-slate-500">Structure → Momentum → Trend → Scan</p>
        </div>

        <div className="reveal mt-8 flex justify-center">
          <Button
            size="lg"
            className="rounded-full border border-cyan-300/45 bg-cyan-300/[0.08] px-7 font-black text-cyan-50 shadow-[0_0_32px_rgba(51,205,211,0.14)] hover:border-cyan-200/70 hover:bg-cyan-300/15 hover:text-white"
            asChild
          >
            <Link href="/product" className="inline-flex items-center gap-2">
              Explore all tools
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

export function StructuraHomePage() {
  return (
    <>
      <StructuraSoftwareSchema />
      <PageShell hero={<Hero2Section />} showPreferredSources>
      <SEO
        title="Structura - Market Structure Intelligence for Traders"
        description="Read market structure before the move becomes obvious. Structura gives traders a framework for structure, momentum, trend, and scan across any market."
        path="/"
      />
      <RevealController />
      <HomeOverviewVideoSchema />
      <HomeOverviewVideoSection />
      <SocialProofBar />
      <CoreValueSection />
      <ProductWorkflowSection />
      <PrismHomeReadSection />
      <WhyBuiltStructuraSection />
      <PlatformSection />
      <section className="bg-[#020617] py-16 lg:py-20">
        <div className="container">
          <SectionHeader
            eyebrow="Conversion"
            title="Start with three free tools - move into the full system when you need more."
            body="Free Accumulate, Divergence, and Blocks lower the entry barrier, while Compass, Vector, and Vantage guide users into the broader Horizon, Pulse, and Sentinel workflow."
          />
          <div className="reveal mx-auto max-w-4xl rounded-[2rem] border border-cyan-300/20 bg-cyan-300/[0.06] p-7 text-center shadow-[0_0_80px_rgba(51,205,211,0.12)]">
            <h3 className="text-3xl font-black text-white">
              Ready for a smarter way to trade?
            </h3>
            <p className="mx-auto mt-4 max-w-2xl text-slate-300">
              Get Accumulate, Divergence, and Blocks free, compare the paid tiers, or start the
              Compass, Vector, or Vantage workflow.
            </p>
            <Button
              className="mt-7 rounded-xl bg-[#33CDD3] px-8 font-bold text-[#061018] hover:bg-[#69e5e9]"
              asChild
            >
              <Link href="/pricing">Go to pricing</Link>
            </Button>
          </div>
        </div>
      </section>
      </PageShell>
    </>
  );
}

function SocialProofBar() {
  return (
    <section className="border-y border-white/10 bg-white/[0.025] py-5 overflow-hidden">
      <div className="flex items-center justify-center gap-3 px-4 whitespace-nowrap">
        <span className="text-[0.65rem] sm:text-sm font-black uppercase tracking-[0.2em] sm:tracking-[0.32em] text-slate-200">STRUCTURE</span>
        <span className="text-[0.65rem] sm:text-sm font-black text-[#33CDD3]/60">→</span>
        <span className="text-[0.65rem] sm:text-sm font-black uppercase tracking-[0.2em] sm:tracking-[0.32em] text-slate-200">MOMENTUM</span>
        <span className="text-[0.65rem] sm:text-sm font-black text-[#33CDD3]/60">→</span>
        <span className="text-[0.65rem] sm:text-sm font-black uppercase tracking-[0.2em] sm:tracking-[0.32em] text-slate-200">TREND</span>
      </div>
    </section>
  );
}

function ComparisonRow({
  index,
  label,
  left,
  right,
}: {
  index: number;
  label: string;
  left: { title: string; body: string };
  right: { title: string; body: string };
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.07] last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-4 py-4 text-left transition-colors duration-150 hover:bg-white/[0.02]"
        aria-expanded={open}
      >
        <span className="w-6 shrink-0 text-xs font-black tabular-nums text-slate-600">
          {String(index).padStart(2, "0")}
        </span>
        <span className="flex-1 text-sm font-semibold tracking-wide text-slate-200">
          {label}
        </span>
        <span
          className="shrink-0 text-slate-500 transition-transform duration-200"
          style={{ transform: open ? "rotate(45deg)" : "rotate(0deg)" }}
        >
          <Plus className="h-4 w-4" />
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-200 ease-out"
        style={{ maxHeight: open ? "240px" : "0px", opacity: open ? 1 : 0 }}
      >
        <div className="grid gap-px pb-5 sm:grid-cols-2">
          <div className="border border-cyan-300/10 bg-cyan-300/[0.04] p-5">
            <p className="mb-2 text-[10px] font-black uppercase tracking-[0.32em] text-[#33CDD3]/70">
              Structura
            </p>
            <p className="text-sm font-bold text-white">{right.title}</p>
            <p className="mt-2 text-sm leading-6 text-slate-400">{right.body}</p>
          </div>
          <div className="border border-white/[0.06] bg-[#080c14] p-5">
            <p className="mb-2 text-[10px] font-black uppercase tracking-[0.32em] text-rose-400/70">
              Traditional
            </p>
            <p className="text-sm font-bold text-slate-300">{left.title}</p>
            <p className="mt-2 text-sm leading-6 text-slate-500">{left.body}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CoreValueSection() {
  const chaos = [
    "Too many tools",
    "Conflicting signals",
    "Late reactions",
    "No repeatable process",
    "Isolated outputs",
    "More noise than context",
    "Manual chart scanning",
    "No clear market phase framework",
    "You connect the dots yourself",
  ];
  const structura = [
    "One framework",
    "Structure-first analysis",
    "Momentum context",
    "Trend behavior confirmation",
    "Repeatable workflow",
    "Cleaner decision-support",
    "Market scanning with Sentinel",
    "Structure → Momentum → Trend",
    "The read written for you - Prism",
  ];

  const rows = [
    {
      label: "Fragmented indicators vs one framework",
      left: {
        title: "Multiple disconnected tools",
        body: "Traders stack RSI, MACD, moving averages, and volume indicators independently. Each tool answers a different question with no shared logic connecting them.",
      },
      right: {
        title: "One structured workflow",
        body: "Structura organizes analysis into a single framework: Structure → Momentum → Trend. Every layer answers a specific question in a defined order.",
      },
    },
    {
      label: "Late reactions vs early structure",
      left: {
        title: "Reacting after the move",
        body: "Most indicator setups trigger after price has already moved. By the time a signal fires, the high-probability entry has passed.",
      },
      right: {
        title: "Reading structure before expansion",
        body: "Accumulate identifies where price may be rebuilding before expansion becomes visible. Structure is read first, momentum is confirmed second.",
      },
    },
    {
      label: "Isolated signals vs market context",
      left: {
        title: "Signals without context",
        body: "A bullish crossover on RSI means nothing without knowing whether structure supports it. Isolated signals produce conflicting reads across timeframes.",
      },
      right: {
        title: "Layered market context",
        body: "Each Structura layer adds context to the previous one. Structure sets the foundation, Horizon adds momentum state, Pulse confirms trend behavior.",
      },
    },
    {
      label: "Manual scanning vs Sentinel",
      left: {
        title: "Scanning charts manually",
        body: "Without a systematic scanning process, traders miss setups or spend hours reviewing charts one by one across markets and timeframes.",
      },
      right: {
        title: "Sentinel scanning layer",
        body: "Sentinel is the fourth layer of Structura, designed to identify where to focus next across markets. It replaces manual scanning with a structured process.",
      },
    },
    {
      label: "Signal chasing vs decision-support",
      left: {
        title: "Chasing the next signal",
        body: "Indicator-driven traders wait for the next alert or crossover. This creates reactive behavior and inconsistent execution across different market conditions.",
      },
      right: {
        title: "Structured decision-support",
        body: "Structura does not replace judgment. It improves clarity, context, and decision quality by providing a consistent analytical process before any trade is considered.",
      },
    },
    {
      label: "Generic indicators vs structure-specific workflow",
      left: {
        title: "Generic, multi-purpose indicators",
        body: "Most indicators were designed for general use. They are not built around market structure logic or the specific sequence of how price transitions.",
      },
      right: {
        title: "Purpose-built for structure analysis",
        body: "Every Structura tool is designed around one question: what is the market doing structurally? Accumulate, Horizon, Pulse, and Sentinel each answer a specific part of that question.",
      },
    },
    {
      label: "Noise vs clarity",
      left: {
        title: "More data, more noise",
        body: "Adding more indicators increases the number of conflicting signals. Traders end up with more information but less clarity about what the market is actually doing.",
      },
      right: {
        title: "Fewer layers, cleaner reads",
        body: "Structura reduces the analytical surface to four structured layers. Each layer has a defined role, which reduces noise and improves the quality of market reads.",
      },
    },
    {
      label: "One timeframe vs multi-timeframe context",
      left: {
        title: "Single-timeframe analysis",
        body: "Most indicator setups are applied to a single chart. Without higher-timeframe context, traders miss the broader structural picture that drives price behavior.",
      },
      right: {
        title: "Multi-timeframe structure reading",
        body: "Structura is designed to be applied across timeframes. Structure on a higher timeframe provides context for momentum and trend reads on a lower one.",
      },
    },
  ];

  return (
    <section className="relative overflow-hidden bg-[#020617] py-16 lg:py-24">
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute inset-0 structura-grid opacity-[0.08]" />
        <div className="absolute left-1/2 top-0 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-cyan-300/10 blur-[130px]" />
        <div className="absolute right-[-10rem] bottom-[-12rem] h-[28rem] w-[28rem] rounded-full bg-[#33CDD3]/12 blur-[120px]" />
      </div>

      <div className="container relative">
        {/* Header */}
        <div className="reveal mx-auto max-w-5xl text-center">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.42em] text-[#33CDD3]">The problem</p>
          <h2 className="mx-auto max-w-4xl text-4xl font-black tracking-[-0.05em] text-white sm:text-5xl lg:text-6xl">
            Most traders react too late.
          </h2>
          <p className="mx-auto mt-6 max-w-4xl text-lg leading-8 text-slate-300 sm:text-xl sm:leading-9">
            They add more indicators, chase momentum, and lose the context
            behind the move. Structura gives traders a cleaner way to read what happens
            before expansion becomes obvious.{" "}
            <span className="font-semibold text-[#33CDD3]">
              Less indicator chaos. More structure.
            </span>
          </p>
        </div>

        {/* Top two-card comparison */}
        <div className="mt-14 grid gap-px lg:grid-cols-2">
          <div className="reveal border border-cyan-300/20 bg-cyan-300/[0.06] p-7 shadow-[0_0_60px_rgba(51,205,211,0.08)] sm:p-9">
            <p className="text-[10px] font-black uppercase tracking-[0.42em] text-[#33CDD3]">
              Structura
            </p>
            <div className="mt-7 grid gap-3">
              {structura.map(item => (
                <div key={item} className="flex items-center gap-4 text-white">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-emerald-400/25 bg-emerald-400/12 text-emerald-400">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm font-semibold">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal border border-white/[0.07] bg-[#080c14] p-7 sm:p-9">
            <p className="text-[10px] font-black uppercase tracking-[0.42em] text-slate-500">
              Traditional indicator chaos
            </p>
            <div className="mt-7 grid gap-3">
              {chaos.map(item => (
                <div
                  key={item}
                  className="flex items-center gap-4 text-slate-400"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-rose-400/20 bg-rose-400/10 text-rose-400">
                    <X className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm font-semibold">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Expandable comparison module */}
        <div className="reveal mt-16">
          <div className="mb-2">
            <p className="text-[10px] font-black uppercase tracking-[0.42em] text-slate-500">
              Detailed comparison
            </p>
            <h3 className="mt-4 text-2xl font-black tracking-[-0.03em] text-white sm:text-3xl">
              From indicator chaos to structure-first analysis.
            </h3>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-400">
              Structura replaces fragmented indicators with one repeatable workflow
              for reading structure, momentum, and trend behavior.
            </p>
          </div>

          <div className="mt-8 border-t border-white/[0.07]">
            {rows.map((row, i) => (
              <ComparisonRow
                key={row.label}
                index={i + 1}
                label={row.label}
                left={row.left}
                right={row.right}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductToolVideos() {
  return (
    <section aria-label="Tool explainer videos" className="bg-[#050d1a] py-12 sm:py-16">
      <div className="container">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {(["accumulate", "divergence", "blocks", "sentinel", "pulse"] as const).map(slug => {
            const video = toolVideos[slug];
            const name = `Structura ${slug.charAt(0).toUpperCase()}${slug.slice(1)}`;
            return <VideoCard key={slug} videoId={video.videoId} title={name} description={video.caption} href={`/tools/${slug}`} />;
          })}
        </div>
        <a href="https://www.youtube.com/playlist?list=PLDIUX_hWzSYU" target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex text-sm font-semibold text-[#33CDD3] transition hover:text-white">
          All tool videos on YouTube
        </a>
      </div>
    </section>
  );
}

export function StructuraProductPage() {
  return (
    <PageShell>
      <SEO
        title="Structura - Market structure layers and Prism, the AI market structure assistant"
        description="Structura market structure layers with Prism, an AI assistant that reads every layer and writes the read. Learn how the workflow fits together."
        path="/product"
      />
      <RevealController />
      <PageHero
        eyebrow="About the product"
        hideBadge
        title="One system for reading market transitions."
        body="Structura combines Accumulate, Horizon, Pulse, and Sentinel into a structured workflow for reading structure, momentum, trend behavior, and market conditions."
        frameworkLine="Structure → Momentum → Trend → Scan"
        icon={Zap}
      />
      <ProductToolVideos />
      <PrismProductSection />
      <ProductWorkflowSection showDetailPanel showHeader={false} />
      <MoreLayersSection />
      <TechnicalSpecificationsSection />
    </PageShell>
  );
}

export function StructuraPricingPage() {
  // SEO injected below in return
  const [billing, setBilling] = useState<BillingPeriod>("monthly");
  const [hoveredComparisonPlan, setHoveredComparisonPlan] = useState<number | null>(null);
  const comparisonPlanLabels = ["Compass", "Vector", "Vantage"] as const;
  const comparisonRows = [
    ["Prism - AI assistant", false, true, true],
    ["Prism reads per month", "—", "100", "150"],
    ["Accumulate (free)", true, true, true],
    ["Divergence (free)", true, true, true],
    ["Blocks (free)", true, true, true],
    ["Horizon", true, true, true],
    ["Pulse", true, true, true],
    ["Sentinel", false, true, true],
    ["Liquidity (new)", false, true, true],
    ["Squeeze (new)", false, true, true],
    ["TradingView access", true, true, true],
    ["TrendSpider compatible", "own sub", "own sub", "own sub"],
    ["Multi-timeframe workflow", true, true, true],
    ["Weekly education", false, true, true],
    ["Community (Telegram + subscriber Discord)", false, true, true],
    ["Support level", "Standard", "Priority", "Exclusive"],
    ["Onboarding call", false, false, true],
    ["Trial availability", "14-day trial", "14-day trial", "14-day trial"],
    ["Cancel anytime", true, true, true],
  ] as const;

  const renderComparisonCell = (
    value: boolean | string,
    feature: string,
    plan: string
  ) => {
    if (typeof value === "boolean") {
      return value ? (
        <Check
          aria-label={`${plan} includes ${feature}`}
          className="mx-auto h-5 w-5 text-[#44BD75]"
        />
      ) : (
        <span className="text-slate-600">—</span>
      );
    }
    if (value === "own sub") {
      return (
        <a
          href="https://trendspider.com/trading-tools-store/collection/trendgo-trading-system/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-[#33CDD3] underline underline-offset-2 hover:text-white transition-colors"
          title="Purchase TrendSpider subscription separately"
        >
          own sub
        </a>
      );
    }
    return <span className="font-mono text-xs text-slate-300">{value}</span>;
  };

  return (
    <PageShell>
      <SEO
        title="Pricing - Structura"
        description="Start free with Accumulate, Divergence, and Blocks. Vector and Vantage add Prism, an AI assistant that reads every layer and writes the read, plus Liquidity, Squeeze, Sentinel, and support."
        path="/pricing"
      />
      <RevealController />
      <section className="relative bg-[#020617] py-20 lg:py-24">
        <div className="pointer-events-none absolute inset-0 structura-grid opacity-[0.16]" />
        <div className="container relative">

          <div className="reveal mx-auto max-w-4xl text-center">
            <h1 className="text-4xl font-black tracking-[-0.055em] text-white md:text-6xl">
              Three plans, named for what they do.
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-300 md:text-xl">
              Compass is the core stack and now starts at 29. Vector adds Prism, Liquidity and Squeeze. Vantage adds the human layer. Annual is ten months for twelve.
            </p>
          </div>

          <div className="reveal mt-12 flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] p-1.5 shadow-[0_0_60px_rgba(51,205,211,0.08)]">
                <button
                  onClick={() => setBilling("monthly")}
                  className={`rounded-full px-5 py-2 text-sm font-bold transition ${billing === "monthly" ? "bg-[#33CDD3] text-[#061018]" : "text-slate-400 hover:text-white"}`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBilling("yearly")}
                  className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition ${billing === "yearly" ? "bg-[#33CDD3] text-[#061018]" : "text-slate-400 hover:text-white"}`}
                >
                  Yearly
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] transition ${
                    billing === "yearly"
                      ? "bg-[#061018]/20 text-[#061018]"
                      : "bg-emerald-400/15 text-emerald-300"
                  }`}>
                    2 mo free
                  </span>
                </button>
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Prices shown are net of VAT. Applicable VAT is calculated in the checkout.
            </p>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {pricingPlans.map(plan => (
              <Card
                key={plan.key}
                className={`reveal relative flex min-h-[28rem] flex-col rounded-[1.75rem] p-7 backdrop-blur-xl ${
                  plan.highlighted
                    ? "border-2 border-[#33CDD3]/80 bg-[#33CDD3]/[0.08] shadow-[0_0_100px_rgba(51,205,211,0.24)] ring-1 ring-[#33CDD3]/30 lg:-translate-y-2"
                    : "border border-white/10 bg-white/[0.045]"
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-[#33CDD3] px-5 py-2 text-[11px] font-black uppercase tracking-widest text-[#061018] shadow-[0_0_24px_rgba(51,205,211,0.45)]">
                    Most Popular
                  </div>
                )}
                <p className="text-base font-black uppercase tracking-[0.28em] text-[#33CDD3]">
                  {plan.name}
                </p>
                <p className="mt-4 min-h-12 text-sm leading-6 text-slate-300">
                  {plan.key === "vector" ? (
                    <>
                      Compass, plus Sentinel, the two new layers and <strong className="font-black text-white">Prism</strong> - the read, written for you, on any chart.
                    </>
                  ) : (
                    plan.description
                  )}
                </p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="text-5xl font-black tracking-[-0.05em] text-white">
                    {billing === "monthly" ? plan.monthly : plan.yearly}
                  </span>
                  <span className="pb-2 text-sm text-slate-400">/{billing === "monthly" ? "mo" : "yr"}</span>
                </div>
                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {plan.label}
                </p>
                <ul className="mt-7 flex-1 space-y-3 text-sm leading-6 text-slate-300">
                  {plan.features.map(feature => (
                    <li key={feature} className={`flex gap-3 ${feature.startsWith("Prism") ? "text-[1.02rem] font-black text-white" : ""}`}>
                      <Check className={`mt-1 h-4 w-4 shrink-0 ${feature.startsWith("Prism") ? "text-[#33CDD3]" : "text-[#44BD75]"}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href={checkoutLinks[plan.key][billing]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-7 flex w-full items-center justify-center rounded-2xl py-5 text-sm font-black uppercase tracking-wider transition ${
                    plan.highlighted
                      ? "bg-[#33CDD3] text-[#061018] shadow-[0_0_30px_rgba(51,205,211,0.28)] hover:bg-[#69e5e9]"
                      : "border border-[#33CDD3]/40 bg-[#33CDD3]/[0.08] text-[#33CDD3] hover:border-[#33CDD3]/70 hover:bg-[#33CDD3]/[0.15]"
                  }`}
                >
                  {plan.cta}
                </a>
              </Card>
            ))}
          </div>

          <p className="reveal mt-8 text-center text-xs leading-6 text-slate-500">
            Already paying? Your price does not change and your plan gets more. Nobody pays more.
          </p>

        </div>
      </section>

      <PricingPrismSection />

      <section className="bg-[#050d1a] py-16 lg:py-20">
        <div className="container">
          <SectionHeader
            eyebrow="Plans Comparison"
            title="Plan comparison"
            body="Check the features we have, compare them to what you need. We make it easier for you to start."
          />
          <div className="reveal overflow-x-auto rounded-[2rem] border border-white/10 bg-white/[0.035] shadow-[0_30px_120px_rgba(2,6,23,0.35)]">
            <div
              className="relative min-w-[900px]"
              onMouseLeave={() => setHoveredComparisonPlan(null)}
            >
              <div className="pointer-events-none absolute inset-0 z-0 grid grid-cols-[1.35fr_repeat(3,1fr)] px-5">
                <span aria-hidden="true" />
                {comparisonPlanLabels.map((plan, index) => (
                  <span
                    key={`comparison-highlight-${plan}`}
                    aria-hidden="true"
                    className={`border-x transition duration-200 ${hoveredComparisonPlan === index ? "border-cyan-300/25 bg-cyan-300/[0.075] shadow-[0_0_60px_rgba(51,205,211,0.12)]" : "border-transparent bg-transparent"}`}
                  />
                ))}
              </div>
              <div className="relative z-10 grid grid-cols-[1.35fr_repeat(3,1fr)] border-b border-white/10 bg-white/[0.04] px-5 py-4 text-xs font-black uppercase tracking-[0.18em] text-[#33CDD3]">
                <span className="text-white">Feature</span>
                {comparisonPlanLabels.map((plan, index) => (
                  <span
                    key={plan}
                    onMouseEnter={() => setHoveredComparisonPlan(index)}
                    className={`rounded-xl py-1 text-center transition duration-200 ${hoveredComparisonPlan === index ? "text-white" : ""}`}
                  >
                    {plan}
                  </span>
                ))}
              </div>
              {comparisonRows.map(
                ([feature, compass, vector, vantage]) => (
                  <div
                    key={feature}
                     className={`relative z-10 grid grid-cols-[1.35fr_repeat(3,1fr)] items-center border-b border-white/10 px-5 py-4 text-center text-sm text-slate-300 last:border-b-0 ${feature.startsWith("Prism") ? "bg-cyan-300/[0.045]" : ""}`}
                  >
                     <span className={`text-left font-bold ${feature.startsWith("Prism") ? "text-[#33CDD3]" : "text-white"}`}>
                      {feature}
                    </span>
                    {[compass, vector, vantage].map((value, index) => (
                      <span
                        key={`${feature}-${index}`}
                        onMouseEnter={() => setHoveredComparisonPlan(index)}
                        className={`rounded-xl py-2 transition duration-200 ${hoveredComparisonPlan === index ? "text-white" : ""}`}
                      >
                        {renderComparisonCell(
                          value,
                          feature,
                          comparisonPlanLabels[index]
                        )}
                      </span>
                    ))}
                  </div>
                )
              )}
            </div>

            {/* CTA buttons under comparison table */}
            <div className="relative z-10 grid grid-cols-[1.35fr_repeat(3,1fr)] items-center border-t border-white/10 bg-white/[0.02] px-5 py-5">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Get started</span>
              {([
                { label: "Get Compass", href: checkoutLinks.compass[billing] },
                { label: "Get Vector", href: checkoutLinks.vector[billing] },
                { label: "Get Vantage", href: checkoutLinks.vantage[billing] },
              ] as const).map(({ label, href }) => (
                <div key={label} className="flex justify-center">
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    <button className="rounded-none border border-[#33CDD3]/40 bg-[#33CDD3]/10 px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#33CDD3] transition hover:border-[#33CDD3]/70 hover:bg-[#33CDD3]/20">
                      {label}
                    </button>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FaqSection
        title="Questions about plans"
        eyebrow="Billing FAQ"
        items={billingFaq}
      />
    </PageShell>
  );
}

export function StructuraAboutPage() {
  return (
    <PageShell>
      <SEO
        title="About - Structura"
        description="Structura was built by traders who spent years working with active markets. Formerly known as TrendGo, we rebranded to reflect what the system truly does: read structure."
        path="/about"
      />
      <RevealController />
      <PageHero
        eyebrow=""
        title="Years of market experience. One structured framework."
        body="Structura - formerly known as TrendGo - was built by traders and technologists who spent years working with active markets and saw the same problem repeat: too many tools, no clear process. We rebranded to reflect what the system truly does: read structure. We built the system we wished existed."
        icon={Users}
        hideBadge
      />
      <WhyBuiltStructuraSection />
      <section className="bg-[#020617] py-16 lg:py-24">
        <div className="container">
          <div className="reveal border-l-2 border-[#33CDD3] pl-8 lg:pl-12">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.42em] text-[#33CDD3]">Our purpose</p>
            <p className="text-3xl font-black leading-[1.15] tracking-[-0.03em] text-white sm:text-4xl lg:text-5xl xl:text-6xl">
              Help traders move from reactive price watching to structured reading of market conditions -{" "}
              <span className="text-[#33CDD3]">one language, one workflow, one repeatable process.</span>
            </p>
          </div>
        </div>
      </section>
      <section className="bg-[#050d1a] py-16 lg:py-20">
        <div className="container">
          <div className="reveal mx-auto mb-10 max-w-2xl text-center">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.42em] text-[#33CDD3]">
              Founders &amp; Team
            </p>
            <h2 className="text-3xl font-black leading-[1.05] tracking-[-0.045em] text-white md:text-5xl">
              A team connecting product,
              <br className="hidden lg:block" />
              technology, and markets.
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {team.map(person => (
              <Card
                key={person.name}
                className="reveal group overflow-hidden rounded-[1.25rem] border border-white/[0.12] bg-[#111a2a] shadow-[0_18px_50px_rgba(0,0,0,0.18)] transition duration-500 hover:-translate-y-1 hover:border-cyan-300/35"
              >
                <div className="relative isolate aspect-[4/5] overflow-hidden bg-[#07101d]">
                  <img
                    src={person.photo}
                    alt={person.name}
                    className={`absolute inset-0 h-full w-full object-cover object-top transition duration-700 ${
                      person.name === "Pierce Crosby"
                        ? "scale-[1.18] origin-center group-hover:scale-[1.22]"
                        : "group-hover:scale-[1.04]"
                    }`}
                    loading="lazy"
                    width={512}
                    height={640}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#07101d]/65 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 rounded-full border border-cyan-300/20 bg-[#07101d]/85 px-3 py-1 text-[0.62rem] font-black uppercase tracking-[0.14em] text-cyan-100 backdrop-blur">
                    {person.label}
                  </div>
                </div>
                <div className="min-h-[210px] bg-[#111a2a] p-5 md:p-6">
                  <h3 className="text-xl font-black tracking-[-0.02em] text-white">
                    {person.name}
                  </h3>
                  <p className="mt-1 text-sm font-semibold leading-5 text-cyan-300">
                    {person.role}
                  </p>
                  <p className="mt-5 text-sm leading-7 text-slate-300">
                    {person.bio}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
      <InteractiveStorySection />
      <ProductUpdatesSection />
    </PageShell>
  );
}

function ProductUpdatesSection() {
  const updates = [
    {
      date: "Jul 2026",
      tag: "Category",
      title: "Structura · Market Structure Intelligence",
      body:
        "TrendGo is rebranded as Structura in July 2026. The new name reflects the system's core purpose: reading market structure. Structura formally establishes Market Structure Intelligence as a distinct analytical category.",
    },
    {
      date: "Feb 2026",
      tag: "Platform",
      title: "TrendSpider support",
      body:
        "TrendSpider availability arrives in February 2026, giving traders a second platform option alongside TradingView and enabling Sentinel scanning within TrendSpider's native environment.",
    },
    {
      date: "Nov 2025",
      tag: "Launch",
      title: "Sentinel launched",
      body:
        "Sentinel (scanning layer) is released in November 2025, completing the four-tool system. The framework now extends from reading individual charts to scanning market-wide conditions.",
    },
    {
      date: "Mar 2025",
      tag: "Launch",
      title: "Public release on TradingView as TrendGo",
      body:
        "The system launches publicly on TradingView under the TrendGo name in March 2025. The release validates trader demand for workflow-first tools that organize analysis rather than replace judgment.",
    },
  ];
  return (
    <section className="bg-[#050d1a] py-16 lg:py-20">
      <div className="container">
        <SectionHeader
          eyebrow="Product updates"
          title="What's new with Structura."
          body="Current milestones, platform updates, and category developments - in chronological order."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {updates.map(u => (
            <div
              key={u.title}
              className="reveal rounded-[1.5rem] border border-cyan-300/15 bg-[#101a2a]/90 p-6 shadow-[0_24px_80px_rgba(2,6,23,0.28)] md:p-8"
            >
              <div className="mb-4 flex items-center gap-3">
                <span className="rounded-full border border-cyan-300/30 bg-cyan-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-[#33CDD3]">
                  {u.tag}
                </span>
                <span className="text-xs font-semibold text-slate-400">{u.date}</span>
              </div>
              <h3 className="text-lg font-black text-white md:text-xl">{u.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-300">{u.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyBuiltStructuraSection() {
  return (
    <section className="bg-[#020617] py-16 lg:py-20">
      <div className="container">
        <div className="overflow-hidden rounded-[2rem] border border-cyan-300/20 bg-[radial-gradient(circle_at_18%_12%,rgba(34,211,238,0.14),transparent_36%),linear-gradient(135deg,rgba(15,23,42,0.98),rgba(2,6,23,0.98))] p-6 shadow-[0_0_90px_rgba(34,211,238,0.1)] md:p-10 lg:p-12">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.32em] text-cyan-300">
                Why We Built Structura
              </p>
              <h2 className="mt-5 max-w-3xl text-4xl font-black leading-tight text-white md:text-5xl">
                Traders don't need more indicators. They need a clearer process.
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 md:text-lg">
                After working with traders across different markets and timeframes,
                one pattern kept appearing: most analysis problems were not about
                missing the right indicator. They were about missing a structured way
                to read what was happening.
              </p>
              <p className="mt-5 max-w-2xl text-base leading-8 text-slate-300 md:text-lg">
                Structura was built to organize that analysis into a visual framework
                - Structure → Momentum → Trend - that could be applied consistently,
                regardless of market or timeframe.
              </p>
            </div>
            <div className="space-y-5">
              <Card className="border border-cyan-300/20 bg-white/[0.06] p-6 shadow-[0_0_55px_rgba(34,211,238,0.08)] backdrop-blur md:p-8">
                <blockquote className="text-lg italic leading-8 text-slate-300">
                  “The goal is not to replace judgment. The goal is to improve
                  clarity, context, and decision quality.”
                </blockquote>
                <div className="mt-7 flex items-center gap-4">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full ring-2 ring-cyan-400/50 shadow-[0_0_28px_rgba(34,211,238,0.35)]">
                    <img
                      src="/team/pawel-surdyk.png"
                      alt="Pawel Surdyk"
                      className="h-full w-full object-cover object-top"
              loading="lazy"
              width={64}
              height={64}
            />
                  </div>
                  <div>
                    <p className="font-black text-white">Pawel Surdyk</p>
                    <p className="text-sm text-slate-500">Co-Founder / CEO</p>
                  </div>
                </div>
              </Card>
              <div className="rounded-2xl border border-white/10 bg-white/[0.045] p-5 transition duration-300 hover:border-cyan-300/30 hover:bg-cyan-300/[0.06]">
                <p className="font-black text-white">Structure → Momentum → Trend</p>
                <p className="mt-1 text-sm text-slate-500">
                  The core framework behind every tool
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.045] p-5 transition duration-300 hover:border-cyan-300/30 hover:bg-cyan-300/[0.06]">
                <p className="font-black text-white">Formerly TrendGo</p>
                <p className="mt-1 text-sm text-slate-500">
                  Rebranded to better reflect the system's purpose
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InteractiveStorySection() {
  const storySteps = [
    {
      year: "2023",
      label: "Early research",
      title: "Market structure research begins",
      copy:
        "Pawel and Marcin begin developing the concept of a structure-first trading framework - identifying the gap between what traders see and what actually drives market transitions.",
      detail:
        "The work starts with one problem: traders have charts, indicators, and opinions, but not one consistent process for reading transitions across markets and timeframes.",
    },
    {
      year: "2024",
      label: "First tool",
      title: "Accumulate released",
      copy:
        "The first iteration of the Accumulate structure curve is developed and tested across multiple markets and timeframes.",
      detail:
        "Accumulate becomes the first practical layer of the framework, focused on structure and the way market context develops before momentum becomes visible.",
    },
    {
      year: "2024",
      label: "System build",
      title: "Horizon and Pulse development",
      copy:
        "The momentum layer (Horizon) and trend behavior layer (Pulse) are built and validated, completing the core Structure → Momentum → Trend framework.",
      detail:
        "The system evolves from one tool into a workflow: structure first, momentum second, trend behavior third, with each layer answering a different market question.",
    },
    {
      year: "2025",
      label: "Platform launch",
      title: "TradingView release as TrendGo",
      copy:
        "The system launches publicly on TradingView under the TrendGo name in March 2025, reaching traders across all markets and timeframes.",
      detail:
        "The public release validates that traders want workflow-first tools that organize analysis instead of replacing their judgment with isolated signals.",
    },
    {
      year: "2025",
      label: "Expansion",
      title: "Sentinel launched",
      copy:
        "Sentinel (scanning layer) is released in November 2025, completing the four-tool system and extending the framework to market-wide scanning.",
      detail:
        "Sentinel extends the framework from reading individual charts to scanning market conditions, helping traders connect context with opportunity discovery.",
    },
    {
      year: "2026",
      label: "New platform",
      title: "TrendSpider support",
      copy:
        "TrendSpider availability arrives in February 2026, giving traders a second platform option alongside TradingView.",
      detail:
        "TrendSpider support broadens the reach of the system and enables Sentinel scanning directly within TrendSpider's native scanning environment.",
    },
    {
      year: "2026",
      label: "Strategic rebrand",
      title: "TrendGo becomes Structura",
      copy:
        "The platform is rebranded from TrendGo to Structura in July 2026, better reflecting the system's purpose: Market Structure Intelligence.",
      detail:
        "The rebrand clarifies the category and positions the product around structure, workflow, and decision quality rather than another indicator set.",
    },
    {
      year: "2026",
      label: "Category creation",
      title: "Market Structure Intelligence",
      copy:
        "Structura establishes Market Structure Intelligence as a distinct analytical category - separate from indicators, signals, and prediction tools.",
      detail:
        "The goal is to define a professional standard for traders who want a clearer process for reading structure, momentum, trend behavior, and market conditions.",
    },
  ];
  const [activeStep, setActiveStep] = useState(0);
  const activeStory = storySteps[activeStep];

  return (
    <section className="bg-[#020617] py-16 lg:py-20">
      <div className="container">
        <SectionHeader
          eyebrow="Our Story"
          title="From idea to system."
          body="Click through the milestones to see how a structure-first trading idea became a Market Structure Intelligence system."
        />
        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <Card className="overflow-hidden rounded-[2rem] border border-cyan-300/15 bg-transparent p-0 shadow-none">
            <div className="relative pl-8 md:pl-10">
              <div className="absolute left-[0.65rem] top-3 h-[calc(100%-1.5rem)] w-px bg-cyan-300/45 shadow-[0_0_18px_rgba(34,211,238,0.55)] md:left-[0.78rem]" />
              {storySteps.map((step, index) => {
                const isActive = activeStep === index;
                return (
                  <button
                    key={`${step.year}-${step.label}-${step.title}`}
                    type="button"
                    onClick={() => setActiveStep(index)}
                    className={`group relative mb-8 block w-full rounded-2xl border p-5 text-left transition duration-300 last:mb-0 md:p-6 ${
                      isActive
                        ? "border-cyan-300/35 bg-cyan-300/[0.07] shadow-[0_0_45px_rgba(34,211,238,0.1)]"
                        : "border-transparent bg-transparent hover:border-cyan-300/20 hover:bg-white/[0.04]"
                    }`}
                  >
                    <span
                      className={`absolute -left-[1.98rem] top-6 h-4 w-4 rounded-full transition duration-300 md:-left-[2.2rem] ${
                        isActive
                          ? "bg-cyan-300 shadow-[0_0_24px_rgba(34,211,238,0.85)]"
                          : "bg-cyan-300 shadow-[0_0_18px_rgba(34,211,238,0.55)] group-hover:scale-125"
                      }`}
                    />
                    <span className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">
                      {step.year} - {step.label}
                    </span>
                    <h3 className="mt-3 text-xl font-black text-white">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-slate-400 md:text-base">
                      {step.copy}
                    </p>
                  </button>
                );
              })}
            </div>
          </Card>
          <Card className="sticky top-24 overflow-hidden rounded-[2rem] border border-cyan-300/20 bg-[radial-gradient(circle_at_20%_15%,rgba(34,211,238,0.16),transparent_34%),rgba(15,23,42,0.94)] p-8 shadow-[0_0_80px_rgba(34,211,238,0.1)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.28em] text-cyan-300">
                  Selected milestone
                </p>
                <h3 className="mt-3 text-4xl font-black text-white">
                  {activeStory.year}
                </h3>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-300/25 bg-cyan-300/[0.08] text-cyan-200">
                <Clock3 className="h-6 w-6" />
              </div>
            </div>
            <div className="mt-8 rounded-[1.5rem] border border-white/10 bg-slate-950/45 p-6">
              <p className="text-sm font-black uppercase tracking-[0.22em] text-cyan-300">
                {activeStory.label}
              </p>
              <h4 className="mt-4 text-3xl font-black leading-tight text-white">
                {activeStory.title}
              </h4>
              <p className="mt-5 text-base leading-8 text-slate-300">
                {activeStory.detail}
              </p>
            </div>
            <div className="mt-6 flex items-center gap-1.5">
              {storySteps.map((step, index) => (
                <button
                  key={`dot-${step.year}-${step.label}`}
                  type="button"
                  onClick={() => setActiveStep(index)}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                    index <= activeStep ? "bg-cyan-400" : "bg-white/15 hover:bg-cyan-300/40"
                  }`}
                  aria-label={`Show ${step.year} ${step.label} story milestone`}
                />
              ))}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}

function ResultsTestimonialsSection() {
  return (
    <section className="relative overflow-hidden bg-[#020617] py-16 lg:py-24">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/35 to-transparent" />
      <div className="pointer-events-none absolute left-1/2 top-24 h-72 w-72 -translate-x-1/2 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="container relative">
        <Card className="reveal relative mt-10 overflow-hidden rounded-[2rem] border border-cyan-300/15 bg-gradient-to-br from-slate-950 via-slate-900 to-[#071827] p-7 shadow-[0_30px_120px_rgba(8,145,178,0.16)] lg:p-12">
          <div className="absolute -left-16 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute right-0 top-0 h-44 w-44 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="relative grid items-center gap-8 lg:grid-cols-[140px_1fr] xl:grid-cols-[150px_1fr_auto]">
            <div className="mx-auto h-32 w-32 overflow-hidden rounded-full border border-cyan-200/35 bg-cyan-300/15 p-1 shadow-[0_0_55px_rgba(51,205,211,0.45)] lg:mx-0 lg:h-36 lg:w-36">
              <img
                src={featuredStefanoTestimonial.photo}
                alt="Stefano Bottaioli testimonial portrait"
                className="h-full w-full rounded-full object-cover"
              loading="lazy"
            />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="border-cyan-300/25 bg-cyan-300/10 text-cyan-100">
                  {featuredStefanoTestimonial.label}
                </Badge>
                <div className="flex gap-1 text-yellow-300" aria-label="Five star rating">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Star key={index} className="h-4 w-4 fill-current" />
                  ))}
                </div>
              </div>
              <blockquote className="mt-6 max-w-4xl text-2xl font-black leading-snug text-white md:text-3xl">
                "
                {(() => {
                  const h = featuredStefanoTestimonial.highlight;
                  const parts = h ? featuredStefanoTestimonial.quote.split(h) : null;
                  return parts && parts.length > 1 ? (
                    <>{parts[0]}<span className="text-[#33CDD3]">{h}</span>{parts[1]}</>
                  ) : featuredStefanoTestimonial.quote;
                })()}
                "
              </blockquote>
              <div className="mt-7">
                <p className="text-lg font-black text-white">
                  {featuredStefanoTestimonial.name}
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-400 md:text-base">
                  {featuredStefanoTestimonial.role}
                </p>
              </div>
            </div>
            <Button asChild className="justify-self-start rounded-full bg-white/10 px-6 text-white hover:bg-white/15 xl:justify-self-end">
              <a href="https://stefano-bottaioli.ghost.io/" target="_blank" rel="noopener noreferrer">
                Read Stefano's Blog <ArrowRight className="ml-2 h-4 w-4 text-[#33CDD3]" />
              </a>
            </Button>
          </div>
        </Card>

        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          {resultsTestimonials.map(item => {
            const parts = item.highlight ? item.quote.split(item.highlight) : [item.quote];
            return (
              <Card
                key={item.name}
                className="reveal rounded border border-white/10 bg-white/[0.045] p-7 transition duration-300 hover:border-cyan-300/25"
              >
                <p className="text-base leading-8 text-slate-300">
                  "
                  {parts.length > 1 ? (
                    <>
                      {parts[0]}
                      <span className="font-bold text-[#33CDD3]">{item.highlight}</span>
                      {parts[1]}
                    </>
                  ) : (
                    item.quote
                  )}
                  "
                </p>
                <div className="mt-7 flex items-center gap-4">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-cyan-300 to-blue-500 text-sm font-black text-slate-950">
                    {item.initials}
                  </div>
                  <div>
                    <p className="font-black text-white">{item.name}</p>
                    <p className="text-xs font-semibold text-slate-500">{item.role}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <Card className="reveal mt-6 overflow-hidden rounded border border-cyan-300/20 bg-gradient-to-br from-slate-950 via-slate-900 to-[#071827] p-6 transition duration-300 hover:border-cyan-300/35 md:p-7">
          <div className="grid items-center gap-6 md:grid-cols-[180px_1fr]">
            <img
              src="/manus-storage/vtad45_s26_4x5_c274eaaf.png"
              alt="Opening page of VTAD-News No. 45, September 2026, featuring Structura's article Wenn die Ebenen sich widersprechen"
              className="mx-auto w-full max-w-[180px] rounded-xl border border-white/10 object-cover"
              loading="lazy"
            />
            <div>
              <Badge className="border-cyan-300/25 bg-cyan-300/10 text-cyan-100">
                In print
              </Badge>
              <h3 className="mt-4 text-2xl font-black text-white md:text-3xl">
                VTAD-News · No. 45 · September 2026
              </h3>
              <p className="mt-4 text-base leading-8 text-slate-300">
                Six pages in the magazine of VTAD, Germany's association of technical analysts - "Wenn die Ebenen sich widersprechen" (When the Layers Disagree), on reading market structure in layers.
              </p>
              <Link
                href="/read/when-the-layers-disagree"
                className="mt-5 inline-flex items-center font-bold text-[#33CDD3] transition-colors hover:text-white"
              >
                Read the English version <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </Card>

        <p className="mt-8 text-center text-xs italic text-slate-500">
          Individual experiences of members. Structura describes market structure; it does not give buy or sell recommendations, and past results say nothing about future ones.
        </p>
      </div>
    </section>
  );
}

export function StructuraTestimonialsPage() {
  return (
    <PageShell hideFooterCta>
      <SEO
        title="Reviews - Structura"
        description="Real feedback from traders who use Structura. Community reviews, professional insights, and retrospective case studies from the TrendGo community."
        path="/testimonials"
      />
      <RevealController />
      <PageHero
        eyebrow="Reviews & case studies"
        hideBadge
        title="Real feedback from traders who use Structura."
        body="Community reviews, professional insights, and retrospective case studies - all in one place. Structura was formerly known as TrendGo; the community and the feedback carried over."
        icon={MessageCircle}
      />
      <ResultsTestimonialsSection />
      <section className="bg-[#050d1a] py-16 lg:py-20">
        <div className="container">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {caseStudies.map(study => (
              <Card
                key={study.asset}
                className="reveal overflow-hidden rounded border border-white/10 bg-white/[0.045] transition duration-300 hover:border-cyan-300/30"
              >
                <img
                  src={study.image}
                  alt={`${study.asset} case study chart`}
                  className="h-48 w-full object-cover"
              loading="lazy"
            />
                <div className="p-6">
                  <Badge className="border-cyan-300/20 bg-cyan-300/10 text-cyan-100">
                    {study.asset}
                  </Badge>
                  <h3 className="mt-4 text-2xl font-black text-white">
                    {study.title}
                  </h3>
                  <p className="mt-3 text-sm font-semibold text-emerald-200">
                    {study.metric}
                  </p>
                  <p className="mt-4 text-sm leading-7 text-slate-300">
                    {study.copy}
                  </p>
                  <p className="mt-4 text-xs text-slate-500">
                    Retrospective only. Not financial advice.
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#020617] py-16 lg:py-20">
        <div className="container">
          <div className="reveal mx-auto max-w-4xl rounded border border-cyan-300/20 bg-cyan-300/[0.06] p-7 text-center shadow-[0_0_80px_rgba(51,205,211,0.12)]">
            <h3 className="text-3xl font-black text-white">
              Ready to start trading with structure?
            </h3>
            <p className="mx-auto mt-4 max-w-2xl text-slate-300">
              Join traders who use Structura as part of their daily workflow.
            </p>
            <Button
              className="mt-7 rounded bg-[#33CDD3] px-8 font-bold text-[#061018] hover:bg-[#69e5e9]"
              asChild
            >
              <Link href="/pricing">Purchase a plan / Start free trial</Link>
            </Button>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

export function StructuraContactPage() {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      const res = await fetch("https://formspree.io/f/mbdvnzja", {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        setSent(true);
      } else {
        const json = await res.json();
        setError(json?.errors?.[0]?.message ?? "Something went wrong. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <PageShell>
      <SEO
        title="Contact - Structura"
        description="Questions about a plan, platform access, or the product? Reach the Structura team via the contact form or email contact@structuramarkets.com."
        path="/contact"
      />
      <RevealController />
      <PageHero
        eyebrow="Contact us"
        title="Talk to the team directly."
        body="Questions about a plan, platform access, or the product? Use the form below or email us at contact@structuramarkets.com. We respond within 1-2 business days."
        icon={Mail}
      />
      <section className="bg-[#020617] py-16 lg:py-20">
        <div className="container grid gap-8 lg:grid-cols-[1fr_0.8fr]">
          <Card className="reveal rounded-[2rem] border border-white/10 bg-white/[0.045] p-7">
            <h2 className="text-3xl font-black text-white">Write to us</h2>
            {sent ? (
              <div className="mt-6 rounded border border-emerald-300/20 bg-emerald-400/10 p-5 text-emerald-100">
                Thank you for your message. We will get back to you at the email address you provided within 1-2 business days.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 grid gap-4">
                <input
                  required
                  name="name"
                  placeholder="Name"
                  className="rounded-2xl border border-white/10 bg-slate-950/70 px-5 py-4 text-white outline-none focus:border-cyan-300/50"
                />
                <input
                  required
                  name="email"
                  type="email"
                  placeholder="Email"
                  className="rounded-2xl border border-white/10 bg-slate-950/70 px-5 py-4 text-white outline-none focus:border-cyan-300/50"
                />
                <select name="subject" className="rounded-2xl border border-white/10 bg-slate-950/70 px-5 py-4 text-white outline-none focus:border-cyan-300/50">
                  <option>Product feedback</option>
                  <option>Plan question</option>
                  <option>Technical support</option>
                  <option>Partnership</option>
                </select>
                <textarea
                  required
                  name="message"
                  rows={6}
                  placeholder="How can we help?"
                  className="rounded-2xl border border-white/10 bg-slate-950/70 px-5 py-4 text-white outline-none focus:border-cyan-300/50"
                />
                {error && (
                  <div className="rounded border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
                )}
                <Button disabled={submitting} className="rounded bg-[#33CDD3] py-6 font-bold text-[#061018] hover:bg-[#69e5e9] disabled:opacity-60">
                  {submitting ? "Sending..." : "Send message"}
                </Button>
              </form>
            )}
          </Card>
          <div className="reveal grid gap-5">
            <ContactCard
              icon={Mail}
              title="Email support"
              copy="contact@structuramarkets.com"
              href="mailto:contact@structuramarkets.com"
            />
            <ContactCard
              icon={Handshake}
              title="Platform & partnership requests"
              copy="Request a platform, integration, or collaboration"
              href="mailto:contact@structuramarkets.com?subject=Platform%20%26%20Partnership%20Request"
            />
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function ContactCard({
  icon: Icon,
  title,
  copy,
  href,
  internal = false,
}: {
  icon: LucideIcon;
  title: string;
  copy: string;
  href: string;
  internal?: boolean;
}) {
  const content = (
    <Card className="rounded-[1.5rem] border border-white/10 bg-white/[0.045] p-6 transition duration-300 hover:-translate-y-1 hover:border-cyan-300/30">
      <Icon className="h-7 w-7 text-[#33CDD3]" />
      <h3 className="mt-5 text-xl font-black text-white">{title}</h3>
      <p className="mt-2 text-sm text-slate-300">{copy}</p>
    </Card>
  );
  return internal ? (
    <Link href={href}>{content}</Link>
  ) : (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
    >
      {content}
    </a>
  );
}

type SupportFaqItem = { q: string; a: React.ReactNode };
type SupportFaqCategory = {
  eyebrow: string;
  title: string;
  description: string;
  items: SupportFaqItem[];
};

const supportFaqCategories: SupportFaqCategory[] = [
  {
    eyebrow: "Getting started",
    title: "Start with the core Structura workflow.",
    description:
      "These answers introduce Structura, the free Accumulate entry point, and the platforms where most users begin working with the system.",
    items: [
      {
        q: "How do I get started?",
        a: (
          <>
            Sign up for Free Accumulate on the homepage or pricing page - no credit card needed. Then add the indicator to your TradingView chart and follow the{" "}
            <a
              href="/manual"
              className="text-[#33CDD3] underline underline-offset-2 hover:text-white transition-colors"
            >
              User Manual
            </a>
            {" "}for a step-by-step walkthrough of every tool, method, and workflow.
          </>
        ),
      },
      {
        q: "What is Structura?",
        a: "Structura is a market structure intelligence platform that helps traders read structure, momentum, and trend behavior through one clean visual framework.",
      },
      {
        q: "How do I get Accumulate?",
        a: "Enter your email on the homepage or pricing page to get Free Accumulate. It’s free forever and needs no credit card.",
      },
      {
        q: "Do I need TradingView?",
        a: "Structura runs on TradingView, so a TradingView account is the most common way to use the tools.",
      },
      {
        q: "Is TrendSpider supported?",
        a: (
          <>
            Yes. The Structura workflow (Accumulate, Horizon, Pulse, Sentinel) is fully compatible with TrendSpider. Note that TrendSpider requires a separate subscription purchased directly from TrendSpider - it is not included in your Structura membership.{" "}
            <a
              href="https://trendspider.com/trading-tools-store/collection/trendgo-trading-system/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#33CDD3] underline underline-offset-2 hover:text-white transition-colors"
            >
              Get Structura tools for TrendSpider →
            </a>
          </>
        ),
      },
    ],
  },
  {
    eyebrow: "Product usage",
    title: "Understand what each Structura layer shows.",
    description:
      "The product questions explain Accumulate, Horizon, Pulse, and Sentinel in the same practical language used across the site.",
    items: [
      {
        q: "What does Accumulate show?",
        a: "Structure zones - areas where price may be stabilizing and rebuilding after weakness or a reset.",
      },
      {
        q: "What does Horizon show?",
        a: "Momentum conditions - whether momentum is washed out, recovering, overheated, or weakening.",
      },
      {
        q: "What does Pulse show?",
        a: "Trend behavior - whether market behavior is confirming the shift after structure and momentum align.",
      },
      {
        q: "What does Sentinel scan?",
        a: "Selected markets and timeframes, surfacing where structure, momentum, and trend behavior conditions may be developing.",
      },
      {
        q: "Can I use only Accumulate?",
        a: "Yes. Accumulate works as a standalone structure layer and is the recommended free starting point.",
      },
    ],
  },
  {
    eyebrow: "Billing",
    title: "Manage trials, plans, and payments clearly.",
    description:
      "Billing answers are written to reduce friction before checkout and clarify plan flexibility after purchase.",
    items: [
      {
        q: "Can I cancel anytime?",
        a: "Yes. You can cancel at any time and keep access through the end of your current billing period. No cancellation fees.",
      },
      {
        q: "How does the free trial work?",
        a: "All paid plans include a 14-day free trial. No credit card is required to start. You will be charged only if you choose to continue after the trial ends.",
      },
      {
        q: "What payment methods are supported?",
        a: "Major credit and debit cards are accepted through our checkout provider (PayPro Global). Standard online payment methods are supported.",
      },
      {
        q: "Can I pay with crypto?",
        a: "Crypto payments are not currently supported. Payments are processed via PayPro Global using major cards and standard online methods. If you have a specific payment question, contact us at contact@structuramarkets.com.",
      },
      {
        q: "How does the refund policy work?",
        a: "Refund requests are handled on a case-by-case basis. If you have an issue with your plan or billing, contact us at contact@structuramarkets.com and we will review your situation and respond within 1-2 business days.",
      },
      {
        q: "Can I switch plans?",
        a: "Yes - upgrade or downgrade anytime. Changes are applied to your next billing cycle.",
      },
    ],
  },
  {
    eyebrow: "Support",
    title: "How to get help and reach the team.",
    description:
      "Find out how to contact the Structura team, what support is included in each plan, and how to submit feedback.",
    items: [
      {
        q: "How can I reach the team?",
        a: "For product questions, plan issues, or general inquiries, email us at contact@structuramarkets.com. For business-related matters, use the same address with \"Business\" in the subject line. We respond within 1-2 business days.",
      },
      {
        q: "What kind of support does each plan include?",
        a: "Free and Compass plans include standard email support. Vector includes priority support with faster response times. Vantage includes exclusive support with direct access to the team, onboarding call, and custom workflow guidance.",
      },
      {
        q: "How do I submit feedback?",
        a: "Use the Contact page to send product feedback directly to the team. You can also email contact@structuramarkets.com with the subject \"Feedback\". We read every message and use it to improve the product.",
      },
    ],
  },
  {
    eyebrow: "Compliance",
    title: "Know exactly what Structura is - and is not.",
    description:
      "Structura is designed as analytical and educational market software, not a signal service or financial-advice provider.",
    items: [
      {
        q: "Is Structura financial advice?",
        a: "No. Structura provides analytical tools and educational market examples. It does not provide financial advice.",
      },
      {
        q: "Does Structura give buy/sell signals?",
        a: "No. Structura highlights market conditions, structure zones, momentum context, and trend behavior - not buy/sell instructions.",
      },
      {
        q: "Can Structura guarantee profitable trades?",
        a: "No. Trading and investing involve risk. Past performance does not guarantee future results.",
      },
      {
        q: "Is Structura the same product as TrendGo?",
        a: "Yes. Structura is the new identity for TrendGo. The tools, access, and core framework remain the same. We changed the name to better reflect what the system does: helping traders read market structure, momentum, and trend behavior.",
      },
    ],
  },
  {
    eyebrow: "Rebranding",
    title: "TrendGo is now Structura.",
    description:
      "Everything you need to know about the rebrand - what changed, what didn't, and what it means for your access and pricing.",
    items: [
      {
        q: "Why the new name?",
        a: "TrendGo described what we did - spot trends. It didn't describe what we built: a system for reading market structure. Structura matches the philosophy: structure over noise.",
      },
      {
        q: "Does anything change for existing members?",
        a: "No. Access, tools, and your price all carry over automatically. TradingView and TrendSpider access continues uninterrupted.",
      },
      {
        q: "Are the new tools really free? What's the catch?",
        a: "Really free, fully functional, no catch. Indicators were never the product - the system is. Membership buys the integrated workflow, the Sentinel scanner, the community, and weekly notes.",
      },
      {
        q: "Is this a new company?",
        a: "No - same team, same product, same billing entity. Only the brand changed.",
      },
      {
        q: "Where did trendgo.io go?",
        a: "It redirects here. Update your bookmarks to structuramarkets.com.",
      },
    ],
  },
];

export function StructuraSupportPage() {
  return (
    <PageShell>
      <SEO
        title="Support & FAQ - Structura"
        description="Find clear guidance for getting started, reading each product layer, managing billing, and understanding Structura's compliance boundaries."
        path="/support"
      />
      <RevealController />
      <PageHero
        eyebrow="Support / FAQ"
        hideBadge
        title="Answers for setting up, using, and managing Structura."
        body="Find clear guidance for getting started, reading each product layer, managing billing, and understanding Structura's compliance boundaries. Can't find what you need? Email us at contact@structuramarkets.com."
        icon={FileQuestion}
      />
      <section className="relative overflow-hidden bg-[#020617] py-16 lg:py-20">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(51,205,211,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(51,205,211,0.05)_1px,transparent_1px)] bg-[size:64px_64px] opacity-70" />
        <div className="container relative z-10">
          {/* Manual quick-link card */}
          <div className="mb-5">
            <a
              href="/manual"
              className="reveal group flex items-center justify-between rounded border border-amber-400/30 bg-amber-400/[0.06] px-6 py-4 transition duration-300 hover:border-amber-400/60 hover:bg-amber-400/[0.1]"
            >
              <div>
                <p className="text-xs font-black uppercase tracking-[0.28em] text-amber-400">User Manual</p>
                <p className="mt-1 text-sm text-slate-300">Full guide to every tool, method, and workflow - with chart examples.</p>
              </div>
              <span className="ml-6 flex shrink-0 items-center gap-2 text-sm font-bold text-amber-300">
                Read the manual
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </a>
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {supportFaqCategories.map(category => (
              <a
                key={category.eyebrow}
                href={`#${category.eyebrow.toLowerCase().replace(/\s+/g, "-")}`}
                className="reveal group rounded border border-white/10 bg-white/[0.045] p-6 transition duration-300 hover:border-cyan-300/35 hover:bg-cyan-300/[0.055]"
              >
                <p className="text-xs font-black uppercase tracking-[0.28em] text-[#33CDD3]">
                  {category.eyebrow}
                </p>
                <h2 className="mt-4 text-xl font-black tracking-[-0.02em] text-white">
                  {category.items.length} questions
                </h2>
                <p className="mt-3 text-sm leading-7 text-slate-300">
                  {category.description}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-cyan-200">
                  Jump to section{" "}
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>
      <CategorizedFaqSection categories={supportFaqCategories} />
      <section className="bg-[#050d1a] py-16 lg:py-20">
        <div className="container">
          <SectionHeader
            eyebrow="Still need help?"
            title="Send us the context and we'll point you to the right next step."
            body="If your question is not covered above, contact the Structura team with your platform, plan, and the Structura layer you are using."
            showEyebrow
          />
          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
            <ContactCard
              icon={Mail}
              title="Email support"
              copy="contact@structuramarkets.com - respond within 1-2 business days"
              href="mailto:contact@structuramarkets.com"
            />
            <ContactCard
              icon={BookOpen}
              title="Education"
              copy="Product notes, market structure education, and case studies"
              href="/education"
              internal
            />
            <ContactCard
              icon={Users}
              title="Contact form"
              copy="Send a message directly to the TrendGo team"
              href="/contact"
              internal
            />
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function CategorizedFaqSection({
  categories,
}: {
  categories: SupportFaqCategory[];
}) {
  return (
    <section className="relative overflow-hidden bg-[#020617] py-16 lg:py-20">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(51,205,211,0.12),transparent_38%)]" />
      <div className="container relative z-10 space-y-14">
        {categories.map(category => (
          <div
            key={category.eyebrow}
            id={category.eyebrow.toLowerCase().replace(/\s+/g, "-")}
            className="scroll-mt-24"
          >
            <div className="reveal mb-5 pl-1">
              <p className="text-xs font-black uppercase tracking-[0.44em] text-[#33CDD3]">
                {category.eyebrow}
              </p>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {category.items.map((item, idx) => (
                <AccordionItem
                  key={item.q}
                  value={`${category.eyebrow}-${idx}`}
                  className="reveal overflow-hidden rounded-[1.35rem] border border-cyan-300/15 bg-[#101a2a]/90 shadow-[0_24px_80px_rgba(2,6,23,0.28)] last:border-b-0"
                >
                  <AccordionTrigger className="px-5 py-5 text-base font-black text-white no-underline hover:no-underline md:px-6 md:text-lg [&>svg]:text-[#33CDD3] [&>svg]:size-5">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 md:px-6 md:pb-6">
                    <p className="max-w-4xl text-sm leading-7 text-slate-300 md:text-base md:leading-8">
                      {item.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </section>
  );
}

function PageHero({
  eyebrow,
  title,
  body,
  frameworkLine,
  icon: Icon,
  hideBadge,
}: {
  eyebrow: string;
  title: string;
  body: string;
  frameworkLine?: string;
  icon: LucideIcon;
  hideBadge?: boolean;
}) {
  return (
    <section className="relative overflow-hidden bg-[#020617] py-20 md:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(51,205,211,0.14),transparent_44%)]" />
      <div className="container relative z-10">
        <div className="reveal mx-auto max-w-4xl text-center">
          {!hideBadge && (
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-sm font-semibold text-slate-300 backdrop-blur-sm">
            <Icon className="h-4 w-4 text-[#33CDD3]" />
            <span>{eyebrow}</span>
          </div>
          )}
          <h1 className="text-4xl font-black leading-tight tracking-[-0.055em] text-white md:text-6xl lg:text-7xl">
            {title}
          </h1>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-8 text-slate-300">
            {body}
          </p>
          {frameworkLine && (
            <p className="mx-auto mt-6 inline-flex rounded-full border border-cyan-300/20 bg-cyan-300/[0.08] px-5 py-3 text-sm font-black tracking-[0.12em] text-cyan-100 shadow-[0_0_40px_rgba(51,205,211,0.12)]">
              {frameworkLine}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function FaqSection({
  title,
  eyebrow = "FAQ",
  items,
}: {
  title: string;
  eyebrow?: string;
  items: { q: string; a: React.ReactNode }[];
}) {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-[#020617] py-16 lg:py-20">
      <div className="container">
        <SectionHeader eyebrow={eyebrow} title={title} />
        <div className="mx-auto max-w-4xl space-y-4">
          {items.map((item, index) => (
            <div
              key={item.q}
              className="reveal overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]"
            >
              <button
                className="flex w-full items-center justify-between gap-4 p-6 text-left"
                onClick={() => setOpen(open === index ? -1 : index)}
              >
                <span className="text-lg font-bold text-white">{item.q}</span>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-cyan-300 transition ${open === index ? "rotate-180" : ""}`}
                />
              </button>
              {open === index && (
                <div className="px-6 pb-6 text-sm leading-7 text-slate-300">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
